/**
 * API Service - Centralized API client with tenant-aware endpoints
 * 
 * This service provides:
 * - Automatic tenant context from URL/localStorage
 * - V2 endpoints with automatic tenant isolation
 * - Fallback to V1 endpoints when needed
 * - Centralized error handling
 */

import axios from 'axios';
import { getMunicipioSlug } from './tenantSlugResolver';

// Re-export so legacy callers that import from services/api keep working.
export { getMunicipioSlug };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with defaults
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get authentication token from various sources.
 * Priority: `token` (canonical, set by AuthContext.login) → admin keys for
 * legacy admin pages. Using `token` first guarantees the Bearer matches the
 * identity shown by the UI (no admin/empreendedor mixing).
 */
const getAuthToken = () => {
  return localStorage.getItem('token')
    || localStorage.getItem('adminToken')
    || localStorage.getItem('admin_token')
    || localStorage.getItem('authToken')
    || null;
};

// Request interceptor - add auth token and tenant context automatically
api.interceptors.request.use(
  (config) => {
    // Defensive: em edge cases raros do axios v1 (ex: cancelamento em série,
    // create com config sem headers), `config.headers` pode vir indefinido.
    // Set aqui evita `TypeError: Cannot set property Authorization of undefined`
    // que se propaga por toda a cadeia de request.
    if (!config.headers) config.headers = {};
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Domain-lock: quando o app está em domínio customizado
    // (ex: empontenova.com.br), enviamos o hostname para o backend.
    // Backend resolve o município via `municipios.custom_domains` e IGNORA
    // qualquer `?municipio_slug=...` que tentar apontar pra outro tenant.
    // Sem esse header, `/negocios` (URL sem slug no path) fica sem tenant
    // resolvido e vaza dados de todos os municípios.
    try {
      if (typeof window !== 'undefined' && window.location?.hostname) {
        const host = window.location.hostname.toLowerCase();
        // Não envia em ambientes de dev/preview genéricos (sem custom domain).
        const isGenericHost =
          host === 'localhost' ||
          host === '127.0.0.1' ||
          host.endsWith('.preview.emergentagent.com') ||
          host.endsWith('.emergentagent.com');
        if (!isGenericHost) {
          config.headers['X-Municipio-Domain'] = host;
        }
      }
    } catch (err) { /* headers opcionais, nunca derruba a request */ }

    // Auto-inject tenant context for v2 endpoints and tenant-sensitive legacy
    // paths (org-projects / org-activities / businesses/social / programs).
    // These devem sempre respeitar o município corrente para evitar "leaks"
    // entre tenants.
    const isV2 = config.url && config.url.includes('/v2/');
    const isTenantSensitiveLegacy = config.url && (
      config.url.startsWith('/org-projects')
      || config.url.startsWith('/org-activities')
      || config.url.startsWith('/businesses/social')
      || config.url === '/programs'
      || config.url.startsWith('/programs?')
    );
    if (isV2 || isTenantSensitiveLegacy) {
      config.params = config.params || {};

      // Skip if already has explicit tenant param
      if (!config.params.municipio_slug && !config.params.municipio_id) {
        // First try: municipality from URL path (public pages)
        const slug = getMunicipioSlug();
        if (slug) {
          config.params.municipio_slug = slug;
        } else {
          // Second try: admin panel selected municipality (from localStorage)
          const saved = localStorage.getItem('admin_selected_municipio');
          if (saved && saved !== 'null' && saved !== 'global') {
            try {
              const parsed = JSON.parse(saved);
              if (parsed?.id) {
                config.params.municipio_id = parsed.id;
              }
            } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
          }
        }
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors globally with auto token refresh
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

// ---- Session-expiry helpers (P1: auto-logout on 401 when refresh isn't possible) ----
// Avoid redirecting on auth/public endpoints (would loop) and only redirect
// when there WAS an Authorization header (i.e. the user had a session).
const _AUTH_ENDPOINT_PATTERNS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/me',
  '/auth/verify-email',
  '/auth/send-verification-email',
  '/auth/2fa',
  '/v2/auth/login',
  '/v2/auth/register',
  '/v2/auth/refresh',
  '/v2/auth/me',
  '/v2/auth/admin/login',
  '/v2/auth/admin/2fa-verify',
];

const _PUBLIC_AUTH_PATHS = [
  '/login',
  '/admin/login',
  '/cadastro',
  '/cadastrar',
  '/aceitar-convite',
  '/verificar-email',
  '/recuperar-senha',
];

let _sessionExpiredHandled = false;

const _shouldAutoLogoutOn401 = (originalRequest) => {
  if (typeof window === 'undefined') return false;
  if (_sessionExpiredHandled) return false;

  const url = originalRequest?.url || '';
  if (_AUTH_ENDPOINT_PATTERNS.some((p) => url.includes(p))) return false;

  // Only act if the failed request actually carried a token (avoids
  // false-positives on truly public endpoints that 401 by accident).
  const hadAuth = !!originalRequest?.headers?.Authorization;
  if (!hadAuth) return false;

  const path = window.location.pathname || '';
  if (_PUBLIC_AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}/`))) return false;

  return true;
};

const _performAutoLogout = (originalRequest) => {
  if (!_shouldAutoLogoutOn401(originalRequest)) return;
  _sessionExpiredHandled = true;

  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refresh_token');
  } catch { /* ignore */ }
  try { _apiCache.clear(); } catch { /* not initialised yet */ }

  // Light toast so user knows what happened
  import('sonner')
    .then(({ toast }) => {
      toast.error('Sua sessão expirou. Faça login novamente.', {
        id: 'session-expired',
      });
    })
    .catch(() => {});

  const path = window.location.pathname || '';
  const isAdminContext = path.startsWith('/admin');
  const target = isAdminContext ? '/admin/login' : '/';
  const returnUrl = encodeURIComponent(path + (window.location.search || ''));
  setTimeout(() => {
    window.location.href = `${target}?session_expired=1&next=${returnUrl}`;
  }, 600);
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle email_nao_verificado (403): dois sabores após iter 227:
    //  • 'email_nao_verificado_strict' → ação restrita mesmo no grace period
    //    (avaliar/comentar/resgatar cupom/editar negócio). Mostra toast e um
    //    call-to-action pra verificar — NÃO redireciona (usuário perderia
    //    contexto).
    //  • 'email_nao_verificado' → grace period expirado (7d+). Aí sim
    //    redireciona pro fluxo obrigatório de OTP.
    // Skip on the verify/register/login endpoints themselves to avoid loops.
    const detail403 = error.response?.status === 403 && error.response?.data?.detail;
    const isStrict = detail403 === 'email_nao_verificado_strict';
    const isExpiredGrace = detail403 === 'email_nao_verificado';
    if ((isStrict || isExpiredGrace) && typeof window !== 'undefined') {
      const url = originalRequest?.url || '';
      const skipPaths = [
        '/auth/verify-email',
        '/auth/send-verification-email',
        '/auth/login',
        '/auth/register',
        '/auth/me',
        '/v2/notifications/',          // background polls não devem redirecionar
        '/v2/admin/system-alerts/',
        '/v2/dashboard/session/',
      ];
      const isSkipped = skipPaths.some((p) => url.includes(p));
      const alreadyThere = window.location.pathname === '/verificar-email';

      if (isSkipped || alreadyThere) {
        return Promise.reject(error);
      }

      if (isStrict) {
        // Ação restrita durante o grace — só avisa com toast, mantém usuário na página.
        try {
          const { toast } = await import('sonner');
          toast.error('Essa ação exige e-mail verificado. Verifique para continuar.', {
            id: 'email-verify-required',
            action: {
              label: 'Verificar agora',
              onClick: () => { window.location.href = '/verificar-email?send=1'; },
            },
          });
        } catch (_) { /* ignore */ }
        return Promise.reject(error);
      }

      // isExpiredGrace: grace period passou → redirect obrigatório.
      try {
        const userRaw = localStorage.getItem('user');
        const email = userRaw ? (JSON.parse(userRaw).email || '') : '';
        if (email) localStorage.setItem('pending_verification_email', email);
      } catch (_) { /* ignore */ }
      window.location.href = '/verificar-email?send=1';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      // Se por algum motivo axios não anexou o config na resposta de erro,
      // não conseguimos retentar — repropaga sem tentar quebrar.
      if (!originalRequest) {
        return Promise.reject(error);
      }
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        _performAutoLogout(originalRequest);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            try {
              if (!originalRequest.headers) originalRequest.headers = {};
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            } catch (e) {
              reject(e);
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        if (!originalRequest.headers) originalRequest.headers = {};
        // Dois formatos de refresh coexistem: JWT stateless (/v2/auth/refresh,
        // emitido pelo registro v2) e rotativo em DB (/auth/refresh, emitido
        // pelo /auth/login). Tenta o v2 e cai para o rotativo se falhar.
        let res;
        try {
          res = await axios.post(`${BACKEND_URL}/api/v2/auth/refresh`, {
            refresh_token: refreshToken,
          });
        } catch (e1) {
          res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });
        }
        const newToken = res.data.access_token;

        // Rotação: persiste o novo refresh_token quando o backend emite um
        if (res.data.refresh_token) {
          localStorage.setItem('refresh_token', res.data.refresh_token);
        }

        // Update stored tokens — atualiza TODOS os keys que existem
        // (não apenas o primeiro), pois `getAuthToken()` lê `token` antes
        // de `adminToken`. Se só um for atualizado, o retry pega o velho
        // e cai em 401 → loop de refresh.
        const tokenKeys = ['adminToken', 'admin_token', 'token', 'authToken'];
        let updatedAny = false;
        for (const key of tokenKeys) {
          if (localStorage.getItem(key)) {
            localStorage.setItem(key, newToken);
            updatedAny = true;
          }
        }
        // Se nenhum existia (edge case: session limpa mas refresh_token ainda válido),
        // grava em `token` como default pra próxima request ter algo.
        if (!updatedAny) {
          localStorage.setItem('token', newToken);
        }

        isRefreshing = false;
        onRefreshed(newToken);
        if (!originalRequest.headers) originalRequest.headers = {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        // Só faz auto-logout se o refresh falhou por erro de autenticação
        // (token inválido/expirado — 401/403). Em falhas transientes de rede
        // (5xx, timeout, no network), NÃO desloga o usuário — apenas repropaga
        // o erro. Assim uma flutuação de rede não gera toast "Sua sessão
        // expirou" indevido.
        const rStatus = refreshError?.response?.status;
        const isAuthFailure = rStatus === 401 || rStatus === 403;
        if (isAuthFailure) {
          _performAutoLogout(originalRequest);
        } else if (typeof window !== 'undefined') {
          // Log defensivo para diagnosticar em prod sem tirar o usuário do fluxo
          try { console.warn('[api] refresh transiente falhou, mantendo sessão:', rStatus, refreshError?.message); } catch (_) { /* ignore */ }
        }
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403) {
      console.warn('Access forbidden - insufficient permissions');
    }
    if (error.response?.status === 429) {
      console.warn('Rate limited - too many requests');
    }
    return Promise.reject(error);
  }
);

/**
 * Get current municipality slug — resolves from React Router (via
 * tenantSlugRef) with a defensive URL fallback. Implementation lives in
 * `services/tenantSlugResolver.js` so it can be unit tested without
 * importing this huge module.
 */

/**
 * Get current tenant ID from URL or localStorage
 */
export const getTenantId = () => {
  return localStorage.getItem('selectedMunicipioId') || null;
};

// ==================== PUBLIC ALERTS API (V2) ====================

export const publicAlertsApi = {
  /**
   * List public alerts (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/public-alerts', { params });
    return response.data;
  },

  /**
   * Track alert interaction (V2 - tenant-aware)
   */
  track: async (alertId, action) => {
    const response = await api.post(`/v2/public-alerts/${alertId}/track`, { action });
    return response.data;
  },
};

// ==================== AUTH API (V2) ====================

export const authApi = {
  /**
   * Admin login (V2 - tenant-aware)
   */
  adminLogin: async (credentials) => {
    const response = await api.post('/v2/auth/admin/login', credentials);
    return response.data;
  },

  admin2faVerify: async (data) => {
    const response = await api.post('/v2/auth/admin/2fa-verify', data);
    return response.data;
  },

  get2faStatus: async () => {
    const response = await api.get('/v2/auth/2fa/status');
    return response.data;
  },

  setup2fa: async () => {
    const response = await api.post('/v2/auth/2fa/setup');
    return response.data;
  },

  verify2fa: async (code) => {
    const response = await api.post('/v2/auth/2fa/verify', { code });
    return response.data;
  },

  disable2fa: async (data) => {
    const response = await api.post('/v2/auth/2fa/disable', data);
    return response.data;
  },

  /**
   * User login (V2 - tenant-aware)
   */
  login: async (credentials) => {
    const response = await api.post('/v2/auth/login', credentials);
    return response.data;
  },

  /**
   * User register (V2 - tenant-aware)
   */
  register: async (userData) => {
    const response = await api.post('/v2/auth/register', userData);
    return response.data;
  },

  /**
   * Get current user (V2 - tenant-aware)
   */
  me: async () => {
    const response = await api.get('/v2/auth/me');
    return response.data;
  },
};

// ==================== LGPD (User Privacy Rights) ====================
// Maps to /app/backend/routes/users_lgpd.py — Art. 18 V/VI/IX endpoints.

export const lgpdApi = {
  /** GET /api/users/me/consent — return consent fingerprint */
  getConsent: async () => {
    const response = await api.get('/users/me/consent');
    return response.data;
  },

  /** POST /api/users/me/revoke-consent — suspend account, revoke refresh tokens */
  revokeConsent: async () => {
    const response = await api.post('/users/me/revoke-consent');
    return response.data;
  },

  /** GET /api/users/me/export — download JSON bundle. Returns a Blob URL the
   *  caller can stick onto an <a download> element. */
  exportData: async () => {
    const response = await api.get('/users/me/export', { responseType: 'blob' });
    return response.data;
  },

  /** DELETE /api/users/me/account — irrevocable anonymisation */
  deleteAccount: async ({ confirmEmail, reason }) => {
    const response = await api.delete('/users/me/account', {
      data: { confirm_email: confirmEmail, reason },
    });
    return response.data;
  },

  /** GET /api/auth/sessions — list active refresh-token sessions */
  listSessions: async () => {
    const response = await api.get('/auth/sessions');
    return response.data;
  },

  /** DELETE /api/auth/sessions/{jti} — revoke a single device */
  revokeSession: async (jti) => {
    const response = await api.delete(`/auth/sessions/${encodeURIComponent(jti)}`);
    return response.data;
  },

  /** POST /api/auth/logout — revoke all sessions (or current refresh token) */
  logoutEverywhere: async () => {
    const response = await api.post('/auth/logout', { everywhere: true });
    return response.data;
  },
};

// ==================== DPO Panel ====================
// All routes are protected by role IN [adm_master, dpo]
export const dpoApi = {
  // Dashboard
  dashboard: async () => (await api.get('/dpo/dashboard')).data,

  // Nomeação
  getNomeacao: async () => (await api.get('/dpo/nomeacao')).data,
  saveNomeacao: async (payload) => (await api.post('/dpo/nomeacao', payload)).data,
  gerarAta: async (id) => api.post(`/dpo/nomeacao/${id}/gerar-ata`, null, { responseType: 'blob' }),

  // Titulares
  listTitulares: async (params = {}) => (await api.get('/dpo/titulares', { params })).data,
  getTitular: async (protocolo) => (await api.get(`/dpo/titulares/${protocolo}`)).data,
  updateTitular: async (protocolo, payload) => (await api.patch(`/dpo/titulares/${protocolo}`, payload)).data,
  // (POST público vive em /api/dpo/titulares sem auth — usado pelo form público)
  createTitularPublico: async (payload) => (await api.post('/dpo/titulares', payload)).data,

  // Incidentes
  listIncidentes: async (params = {}) => (await api.get('/dpo/incidentes', { params })).data,
  getIncidente: async (id) => (await api.get(`/dpo/incidentes/${id}`)).data,
  createIncidente: async (payload) => (await api.post('/dpo/incidentes', payload)).data,
  updateIncidente: async (id, payload) => (await api.patch(`/dpo/incidentes/${id}`, payload)).data,
  gerarComunicacaoAnpd: async (id, tipo = 'preliminar') =>
    api.post(`/dpo/incidentes/${id}/gerar-comunicacao`, null, {
      params: { tipo }, responseType: 'blob',
    }),
  notificarTitulares: async (id, payload) =>
    (await api.post(`/dpo/incidentes/${id}/notificar-titulares`, payload)).data,

  // Mapeamento
  listOperacoes: async () => (await api.get('/dpo/mapeamento')).data,
  createOperacao: async (payload) => (await api.post('/dpo/mapeamento', payload)).data,
  updateOperacao: async (id, payload) => (await api.patch(`/dpo/mapeamento/${id}`, payload)).data,
  deleteOperacao: async (id) => (await api.delete(`/dpo/mapeamento/${id}`)).data,
  gerarRipd: async () => api.post('/dpo/mapeamento/gerar-ripd', null, { responseType: 'blob' }),

  // Sub-processadores
  listSubprocessadores: async () => (await api.get('/dpo/subprocessadores')).data,
  createSubprocessador: async (payload) => (await api.post('/dpo/subprocessadores', payload)).data,
  updateSubprocessador: async (id, payload) => (await api.patch(`/dpo/subprocessadores/${id}`, payload)).data,
  deleteSubprocessador: async (id) => (await api.delete(`/dpo/subprocessadores/${id}`)).data,

  // Treinamentos
  listTreinamentos: async () => (await api.get('/dpo/treinamentos')).data,
  createTreinamento: async (payload) => (await api.post('/dpo/treinamentos', payload)).data,
  deleteTreinamento: async (id) => (await api.delete(`/dpo/treinamentos/${id}`)).data,

  // Templates
  listTemplates: async () => (await api.get('/dpo/templates')).data,
  createTemplate: async (payload) => (await api.post('/dpo/templates', payload)).data,
  updateTemplate: async (id, payload) => (await api.patch(`/dpo/templates/${id}`, payload)).data,
  deleteTemplate: async (id) => (await api.delete(`/dpo/templates/${id}`)).data,

  // Relatórios & Evidências
  logAcoes: async (limit = 200) => (await api.get('/dpo/relatorios/log', { params: { limit } })).data,
  gerarRelatorioAnual: async (ano, observacoes = '') =>
    api.post('/dpo/relatorios/anual', null, {
      params: { ano, observacoes }, responseType: 'blob',
    }),
  listEvidencias: async () => (await api.get('/dpo/evidencias')).data,
  uploadEvidencia: async ({ file, categoria, titulo }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('categoria', categoria);
    fd.append('titulo', titulo);
    const r = await api.post('/dpo/evidencias', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  deleteEvidencia: async (id) => (await api.delete(`/dpo/evidencias/${id}`)).data,

  // Helper: blob download
  downloadBlob: (response, filename) => {
    const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

// Public footer DPO info
export const publicDpoApi = {
  info: async () => (await api.get('/public/dpo/info')).data,
};

// ==================== Recommendations ====================
export const recommendationsApi = {
  /** GET /api/negocios/{id}/recomendacoes — public */
  get: async (negocioId, municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const r = await api.get(
      `/negocios/${encodeURIComponent(negocioId)}/recomendacoes`,
      { params },
    );
    return r.data;
  },

  /** GET /api/negocios/{id}/descoberta — unified discovery endpoint (Sprint 1) */
  getDescoberta: async (negocioId, municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const r = await api.get(
      `/negocios/${encodeURIComponent(negocioId)}/descoberta`,
      { params },
    );
    return r.data;
  },

  /** POST /api/negocios/{id}/track-visit — fire-and-forget */
  trackVisit: async (negocioId, sessionId, municipioId) => {
    try {
      const r = await api.post(`/negocios/${encodeURIComponent(negocioId)}/track-visit`, {
        session_id: sessionId,
        municipio_id: municipioId || null,
      });
      return r.data;
    } catch { return null; }
  },

  /** POST /api/analytics/recommendation-click — fire-and-forget (sendBeacon-safe) */
  trackClick: async (payload) => {
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/analytics/recommendation-click`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const ok = navigator.sendBeacon(url, blob);
        if (ok) return { ok: true, beacon: true };
      }
      const r = await api.post('/analytics/recommendation-click', payload);
      return r.data;
    } catch { return null; }
  },

  // ----- admin -----
  admin: {
    listGrafo: async (categoria_origem) => {
      const params = categoria_origem ? { categoria_origem } : {};
      return (await api.get('/admin/recomendacoes/grafo', { params })).data;
    },
    createGrafo: async (payload) => (await api.post('/admin/recomendacoes/grafo', payload)).data,
    updateGrafo: async (id, payload) => (await api.patch(`/admin/recomendacoes/grafo/${id}`, payload)).data,
    deleteGrafo: async (id) => (await api.delete(`/admin/recomendacoes/grafo/${id}`)).data,
    topCovisitas: async (limit = 20, municipio_id) => {
      const params = { limit };
      if (municipio_id) params.municipio_id = municipio_id;
      return (await api.get('/admin/recomendacoes/covisitas', { params })).data;
    },
    analytics: async (params = {}) =>
      (await api.get('/admin/recomendacoes/analytics', { params })).data,
  },
};

// ==================== BUSINESSES API ====================

export const businessesApi = {
  /**
   * List businesses with automatic tenant filtering
   */
  list: async (params = {}) => {
    const queryParams = { ...params };
    
    // Support both municipio_id and municipio_slug
    // Backend now accepts both, but prefer municipio_id if available
    const tenantId = params.municipio_id || getTenantId();
    if (tenantId) {
      queryParams.municipio_id = tenantId;
    }
    // Pass municipio_slug directly to backend (it will resolve it)
    // This ensures tenant filtering works even when only slug is available
    
    const response = await api.get('/v2/businesses', { params: queryParams });
    return response.data;
  },

  /**
   * Get business statistics
   */
  stats: async (municipioId) => {
    const tenantId = municipioId || getTenantId();
    const params = tenantId ? { municipio_id: tenantId } : {};
    const response = await api.get('/v2/businesses/stats', { params });
    return response.data;
  },

  /**
   * Get single business by ID
   * @param {string} businessId - The business ID
   * @param {string} municipioSlugOrId - Can be either municipio_slug (string like "ponte-nova") or municipio_id
   */
  get: async (businessId, municipioSlugOrId) => {
    // If no tenant context is provided, use v1 API which doesn't require tenant
    // This allows accessing business detail pages directly without municipality in URL
    if (!municipioSlugOrId) {
      const tenantId = getTenantId();
      if (!tenantId) {
        // No tenant context available - use v1 API
        const response = await api.get(`/businesses/${businessId}`);
        return response.data;
      }
    }
    
    const params = {};
    if (municipioSlugOrId) {
      // Check if it looks like a slug (no "mun-" prefix) or an ID
      if (municipioSlugOrId.startsWith('mun-')) {
        params.municipio_id = municipioSlugOrId;
      } else {
        params.municipio_slug = municipioSlugOrId;
      }
    } else {
      // Fall back to getTenantId() if nothing provided
      const tenantId = getTenantId();
      if (tenantId) params.municipio_id = tenantId;
    }
    const response = await api.get(`/v2/businesses/${businessId}`, { params });
    return response.data;
  },

  /**
   * Get businesses owned by current user
   */
  mine: async () => {
    const response = await api.get('/v2/businesses/me/businesses');
    return response.data;
  },

  /**
   * Create new business
   */
  create: async (data) => {
    const response = await api.post('/v2/businesses', data);
    return response.data;
  },

  /**
   * Update business
   */
  update: async (businessId, data) => {
    const response = await api.patch(`/v2/businesses/${businessId}`, data);
    return response.data;
  },

  /**
   * Delete business
   */
  delete: async (businessId) => {
    const response = await api.delete(`/v2/businesses/${businessId}`);
    return response.data;
  },

  /**
   * Get distinct categories
   */
  categories: async (municipioId) => {
    const tenantId = municipioId || getTenantId();
    const params = tenantId ? { municipio_id: tenantId } : {};
    const response = await api.get('/v2/businesses/categories', { params });
    return response.data;
  },

  nearby: async (params = {}) => {
    const response = await api.get('/businesses/nearby', { params });
    return response.data;
  },

  new: async (params = {}) => {
    const response = await api.get('/businesses/new', { params });
    return response.data;
  },

  managementInfo: async (businessId) => {
    const response = await api.get(`/businesses/${businessId}/management-info`);
    return response.data;
  },

  claim: async (businessId, data) => {
    const response = await api.post(`/businesses/${businessId}/claim`, data);
    return response.data;
  },

  claimVerify: async (businessId, claimId, code) => {
    const response = await api.post(`/businesses/${businessId}/claim/${claimId}/verify`, { code });
    return response.data;
  },

  // Admin methods
  admin: {
    pending: async () => {
      const response = await api.get('/v2/businesses/admin/pending');
      return response.data;
    },
    approve: async (businessId) => {
      const response = await api.post(`/v2/businesses/admin/${businessId}/approve`);
      return response.data;
    },
    reject: async (businessId, motivo) => {
      const response = await api.post(`/v2/businesses/admin/${businessId}/reject`, null, {
        params: { motivo }
      });
      return response.data;
    },
  },
};

// ==================== ALERTS API ====================

export const alertsApi = {
  /**
   * Get current user's alerts
   */
  mine: async (params = {}) => {
    const response = await api.get('/v2/alerts/me', { params });
    return response.data;
  },

  /**
   * Count unread alerts
   */
  countUnread: async () => {
    const response = await api.get('/v2/alerts/me/count', { params: { status: 'nao_lido' } });
    return response.data;
  },

  /**
   * Mark alert as read
   */
  markRead: async (alertId) => {
    const response = await api.post(`/v2/alerts/${alertId}/read`);
    return response.data;
  },

  /**
   * Mark all alerts as read
   */
  markAllRead: async () => {
    const response = await api.post('/v2/alerts/mark-all-read');
    return response.data;
  },

  /**
   * Archive an alert
   */
  archive: async (alertId) => {
    const response = await api.delete(`/v2/alerts/${alertId}`);
    return response.data;
  },

  // Public alerts
  public: {
    list: async (municipioId, options = {}) => {
      const tenantId = municipioId || getTenantId();
      const params = { ...options };
      if (tenantId) params.municipio_id = tenantId;
      const response = await api.get('/v2/alerts/public', { params });
      return response.data;
    },
    track: async (alertId, action = 'view') => {
      const response = await api.post(`/v2/alerts/public/${alertId}/track`, null, {
        params: { action }
      });
      return response.data;
    },
  },

  /**
   * List public alerts (simplified for component usage)
   */
  listPublic: async (params = {}) => {
    const response = await api.get('/v2/alerts/public', { params });
    return response.data;
  },

  /**
   * Track public alert interaction
   */
  trackPublic: async (alertId, action = 'view') => {
    const response = await api.post(`/v2/alerts/public/${alertId}/track`, null, {
      params: { action }
    });
    return response.data;
  },

  // Subscriptions
  subscribe: async (data) => {
    const tenantId = data.municipio_id || getTenantId();
    const response = await api.post('/v2/alerts/subscribe', { ...data, municipio_id: tenantId });
    return response.data;
  },

  unsubscribe: async (subscriptionId, email) => {
    const response = await api.delete(`/v2/alerts/subscribe/${subscriptionId}`, {
      params: { email }
    });
    return response.data;
  },

  // Admin
  admin: {
    stats: async () => {
      const response = await api.get('/v2/alerts/admin/stats');
      return response.data;
    },
    listPublic: async () => {
      const response = await api.get('/v2/alerts/admin/public');
      return response.data;
    },
    createPublic: async (data) => {
      const response = await api.post('/v2/alerts/admin/public', null, { params: data });
      return response.data;
    },
    deletePublic: async (alertId) => {
      const response = await api.delete(`/v2/alerts/admin/public/${alertId}`);
      return response.data;
    },
    listSubscriptions: async (status) => {
      const params = status ? { status } : {};
      const response = await api.get('/v2/alerts/admin/subscriptions', { params });
      return response.data;
    },
  },
};

// ==================== DEFESA CIVIL API ====================

export const defesaCivilApi = {
  /** Dispara alerta emergencial. Apenas adm_master/gestor. */
  broadcast: async (payload) => {
    const response = await api.post('/admin/defesa-civil/broadcast', payload);
    return response.data;
  },
  /** Lista alertas de defesa civil emitidos (filtrado por município do gestor). */
  historico: async (params = {}) => {
    const response = await api.get('/admin/defesa-civil/historico', { params });
    return response.data;
  },
  /** Encerra (desativa) um alerta de defesa civil ativo. */
  encerrar: async (alertId) => {
    const response = await api.post(`/admin/defesa-civil/${alertId}/encerrar`);
    return response.data;
  },
  /** Lê URL do canal/grupo WhatsApp configurado para o município. */
  getWhatsappCanal: async (municipioId) => {
    const response = await api.get(`/admin/defesa-civil/municipio/${municipioId}/whatsapp-canal`);
    return response.data;
  },
  /** Salva URL do canal/grupo WhatsApp para o município. */
  setWhatsappCanal: async (municipioId, url) => {
    const response = await api.put(
      `/admin/defesa-civil/municipio/${municipioId}/whatsapp-canal`,
      { whatsapp_canal_url: url || null },
    );
    return response.data;
  },
};

// ==================== PROGRAMS API ====================

export const programsApi = {
  /**
   * List programs/events
   */
  list: async (params = {}) => {
    const tenantId = params.municipio_id || getTenantId();
    const queryParams = { ...params };
    if (tenantId) queryParams.municipio_id = tenantId;
    const response = await api.get('/v2/programs', { params: queryParams });
    return response.data;
  },

  /**
   * Get upcoming programs
   * Aceita `municipioIdOrSlug` — se começar com "mun-" trata como id,
   * senão como slug (o axios interceptor de /v2/* também injeta o slug
   * do tenant ativo se nada for passado).
   */
  upcoming: async (municipioIdOrSlug) => {
    const params = {};
    if (municipioIdOrSlug) {
      if (String(municipioIdOrSlug).startsWith('mun-')) {
        params.municipio_id = municipioIdOrSlug;
      } else {
        params.municipio_slug = municipioIdOrSlug;
      }
    } else {
      const tenantId = getTenantId();
      if (tenantId) params.municipio_id = tenantId;
    }
    const response = await api.get('/v2/programs/upcoming', { params });
    return response.data;
  },

  /**
   * Get program statistics
   */
  stats: async (municipioId) => {
    const tenantId = municipioId || getTenantId();
    const params = tenantId ? { municipio_id: tenantId } : {};
    const response = await api.get('/v2/programs/stats', { params });
    return response.data;
  },

  /**
   * Get single program
   */
  get: async (programId, municipioId) => {
    const tenantId = municipioId || getTenantId();
    const params = tenantId ? { municipio_id: tenantId } : {};
    const response = await api.get(`/v2/programs/${programId}`, { params });
    return response.data;
  },

  /**
   * Create program (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/programs', data);
    return response.data;
  },

  /**
   * Update program (V2 - tenant-aware)
   */
  update: async (programId, data) => {
    const response = await api.put(`/v2/programs/${programId}`, data);
    return response.data;
  },

  /**
   * Delete program (V2 - tenant-aware)
   */
  delete: async (programId) => {
    const response = await api.delete(`/v2/programs/${programId}`);
    return response.data;
  },

  // Admin
  admin: {
    list: async (status) => {
      const params = status ? { status } : {};
      const response = await api.get('/v2/programs/admin/all', { params });
      return response.data;
    },
    create: async (data) => {
      const response = await api.post('/v2/programs', data);
      return response.data;
    },
    update: async (programId, data) => {
      const response = await api.put(`/v2/programs/${programId}`, data);
      return response.data;
    },
    delete: async (programId) => {
      const response = await api.delete(`/v2/programs/${programId}`);
      return response.data;
    },
  },
};

// ==================== CATEGORIES API ====================

// ==================== Lightweight in-memory cache (TTL + in-flight dedup) ====================
// Used to avoid the 3–4x duplicated GETs for /categories and /municipios on the
// Explorar page (the page is shared between /explorar and /<slug>/explorar and
// hooks/MainLayout each trigger a fetch). Cache is per-tab; cleared on reload
// and on logout (see AuthContext.logout).

const _apiCache = new Map(); // key → { value, expiresAt, inFlight: Promise|null }
const _CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

/** Deterministic JSON for cache keys: sorts keys so {a:1,b:2} === {b:2,a:1}. */
const _stableKey = (params) => {
  if (params == null) return '';
  if (typeof params !== 'object') return String(params);
  const keys = Object.keys(params).sort();
  return JSON.stringify(keys.map((k) => [k, params[k]]));
};

const cachedGet = async (key, fetcher) => {
  const now = Date.now();
  const cached = _apiCache.get(key);
  if (cached) {
    if (cached.inFlight) return cached.inFlight;          // dedup concurrent calls
    if (cached.expiresAt > now) return cached.value;       // fresh hit
  }
  const promise = fetcher().then(
    (value) => {
      _apiCache.set(key, { value, expiresAt: Date.now() + _CACHE_TTL_MS, inFlight: null });
      return value;
    },
    (err) => {
      _apiCache.delete(key);  // negative cache would mask transient errors
      throw err;
    },
  );
  _apiCache.set(key, { ..._apiCache.get(key), inFlight: promise });
  return promise;
};

export const invalidateApiCache = (prefix) => {
  for (const k of _apiCache.keys()) {
    if (!prefix || k.startsWith(prefix)) _apiCache.delete(k);
  }
};

export const categoriesApi = {
  /**
   * List all categories
   */
  list: async () => cachedGet('categories:list', async () => {
    const response = await api.get('/categories');
    return response.data;
  }),

  /**
   * Get subcategories for a category — returns array (normalized).
   */
  subcategories: async (categoryId) => {
    const response = await api.get(`/categories/${categoryId}/subcategorias`);
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.subcategorias)) return data.subcategorias;
    return [];
  },

  /**
   * Get tags for a category
   */
  tags: async (categoryId) => {
    const response = await api.get(`/categories/${categoryId}/tags`);
    return response.data;
  },

  // Admin methods
  admin: {
    list: async () => {
      const response = await api.get('/admin/categories');
      return response.data;
    },
    listWithCounts: async () => {
      const response = await api.get('/admin/categories/with-counts');
      return response.data;
    },
    create: async (data) => {
      const response = await api.post('/admin/categories', data);
      invalidateApiCache('categories:');
      return response.data;
    },
    update: async (categoryId, data) => {
      const response = await api.put(`/admin/categories/${categoryId}`, data);
      invalidateApiCache('categories:');
      return response.data;
    },
    delete: async (categoryId) => {
      const response = await api.delete(`/admin/categories/${categoryId}`);
      invalidateApiCache('categories:');
      return response.data;
    },
    toggle: async (categoryId) => {
      const response = await api.patch(`/admin/categories/${categoryId}/toggle`);
      invalidateApiCache('categories:');
      return response.data;
    },

    // ==========================================================
    // Migration tools — dry-run first, then apply. Safe for 10k+
    // businesses (bulk_write batched no backend).
    // ==========================================================
    migration: {
      impact: async (categoryId) => {
        const response = await api.get(`/admin/categories/migration/${categoryId}/impact`);
        return response.data;
      },
      subImpact: async (categoryId, subNome) => {
        const response = await api.get(
          `/admin/categories/migration/${categoryId}/subcategorias/${encodeURIComponent(subNome)}/impact`
        );
        return response.data;
      },
      rename: async (categoryId, body) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/rename`,
          body,
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      merge: async (categoryId, into, dry_run = true) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/merge-into`,
          { into, dry_run },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      deleteSafe: async (categoryId, moveTo = null, dry_run = true) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/delete-safe`,
          { move_to: moveTo, dry_run },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      subRename: async (categoryId, de, para, dry_run = true) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/subcategorias/rename`,
          { de, para, dry_run },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      subMove: async (categoryId, body) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/subcategorias/move`,
          body,
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      subDelete: async (categoryId, body) => {
        const response = await api.post(
          `/admin/categories/migration/${categoryId}/subcategorias/delete-safe`,
          body,
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      logs: async (limit = 50) => {
        const response = await api.get(`/admin/categories/migration/logs?limit=${limit}`);
        return response.data;
      },
      importTaxonomyV2: async (dry_run = true) => {
        const response = await api.post(
          `/admin/categories/migration/import-taxonomy-v2`,
          { dry_run, block_v1_for_new_registrations: true },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      equivalenceMatrix: async () => {
        const response = await api.get(`/admin/categories/migration/equivalence-matrix`);
        return response.data;
      },
      applyMatrix: async ({ min_confianca = 'media', dry_run = true } = {}) => {
        const response = await api.post(
          `/admin/categories/migration/apply-matrix`,
          { min_confianca, dry_run },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
      snapshots: async () => {
        const response = await api.get(`/admin/categories/migration/snapshots`);
        return response.data;
      },
      rollback: async (snapshot_id, dry_run = true) => {
        const response = await api.post(
          `/admin/categories/migration/rollback`,
          { snapshot_id, dry_run },
        );
        invalidateApiCache('categories:');
        return response.data;
      },
    },
  },
};

// ==================== USERS ADMIN API ====================

export const usersAdminApi = {
  /**
   * List users with optional filters
   */
  list: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  /**
   * Get user by ID
   */
  get: async (userId) => {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Create admin user
   */
  create: async (data) => {
    const response = await api.post('/admin/users/create', data);
    return response.data;
  },

  /**
   * Update user
   */
  update: async (userId, data) => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  /**
   * Delete user
   */
  delete: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Set user role
   */
  setRole: async (userId, role) => {
    const response = await api.post(`/admin/users/${userId}/set-role`, null, {
      params: { role }
    });
    return response.data;
  },

  /**
   * Block user
   */
  block: async (userId, reason) => {
    const response = await api.post(`/admin/users/${userId}/block`, null, {
      params: { reason }
    });
    return response.data;
  },

  /**
   * Unblock user
   */
  unblock: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/unblock`);
    return response.data;
  },

  /**
   * Get user stats (V2 - tenant-aware)
   * @param {'empreendedor'|'consumidor'} [tipo] filtra o total ao subgrupo
   */
  stats: async (tipo) => {
    const params = tipo ? { tipo } : {};
    const response = await api.get('/v2/users/admin/stats', { params });
    return response.data;
  },
};

// ==================== ADMIN INVITATIONS API ====================
// Convite por email para cargos administrativos (editor, tecnico, analista, auditor).
export const adminInvitationsApi = {
  /** Cria convite. Body: { email, nome, role, telefone?, cpf?, municipio_id? } */
  invite: async (data) => {
    const response = await api.post('/admin/invitations/invite', data);
    return response.data;
  },
  /** Lista convites (filtro opcional: status='pending'|'accepted'|'expired'|'revoked') */
  list: async (params = {}) => {
    const response = await api.get('/admin/invitations', { params });
    return response.data;
  },
  /** Consulta convite por token (público, sem auth — usado em /aceitar-convite/:token) */
  getByToken: async (token) => {
    const response = await api.get(`/admin/invitations/by-token/${token}`);
    return response.data;
  },
  /** Aceita convite. Body: { password, nome?, telefone?, cpf? } */
  accept: async (token, data) => {
    const response = await api.post(`/admin/invitations/${token}/accept`, data);
    return response.data;
  },
  /** Revoga convite pendente */
  revoke: async (invitationId) => {
    const response = await api.post(`/admin/invitations/${invitationId}/revoke`);
    return response.data;
  },
};

// ==================== ADMIN BUSINESS TRANSFER API ====================
// Cadastro admin de empreendedor+negócio + transferência de titularidade.
export const adminBusinessTransferApi = {
  /** Cria empreendedor + negócio em uma chamada.
   *  Body: { owner_email, owner_nome, owner_telefone?, owner_cpf?, business: {...} } */
  createWithOwner: async (data) => {
    const response = await api.post('/admin/businesses/create-with-owner', data);
    return response.data;
  },
  /** Transfere titularidade. Body: { new_owner_email, reason? } */
  transfer: async (businessId, data) => {
    const response = await api.post(`/admin/businesses/${businessId}/transfer`, data);
    return response.data;
  },
  /** Histórico de transferências */
  history: async (businessId) => {
    const response = await api.get(`/admin/businesses/${businessId}/transfers`);
    return response.data;
  },
  /** Lista negocios com aceites LGPD pendentes.
   *  params: { limit, skip, status_filter: 'all'|'pending'|'expired' } */
  listPendingAceites: async (params = {}) => {
    const response = await api.get('/admin/businesses/pending-aceites', { params });
    return response.data;
  },
  /** Reenvia convite/lembrete de aceites + reseta prazo para 30 dias. */
  resendAceitesInvite: async (businessId) => {
    const response = await api.post(`/admin/businesses/${businessId}/resend-aceites-invite`);
    return response.data;
  },
};

// ==================== ADMIN REPORT SCHEDULES API ====================
// Agendamento de envio de relatórios por email (diário/semanal/mensal).
export const adminReportSchedulesApi = {
  /** Lista tipos de relatório disponíveis. */
  listTypes: async () => (await api.get('/admin/report-schedules/types')).data,
  /** Lista agendamentos. */
  list: async () => (await api.get('/admin/report-schedules')).data,
  /** Cria agendamento. */
  create: async (data) => (await api.post('/admin/report-schedules', data)).data,
  /** Atualiza agendamento. */
  update: async (id, data) => (await api.patch(`/admin/report-schedules/${id}`, data)).data,
  /** Remove agendamento. */
  remove: async (id) => (await api.delete(`/admin/report-schedules/${id}`)).data,
  /** Dispara o agendamento imediatamente (teste). */
  sendNow: async (id) => (await api.post(`/admin/report-schedules/${id}/send-now`)).data,
};

// ==================== ADMIN CONTACTS (Fale Conosco) ====================
export const adminContactsApi = {
  /** Lista mensagens com filtros. params: { status?, categoria?, q?, limit? } */
  list: async (params = {}) => (await api.get('/admin/contacts', { params })).data,
  /** Counts por status e categoria. */
  stats: async () => (await api.get('/admin/contacts/stats')).data,
  /** Atualiza status (pendente | respondido | arquivado). */
  updateStatus: async (id, status) =>
    (await api.patch(`/admin/contacts/${id}/status?status=${encodeURIComponent(status)}`)).data,
  /** Responde a mensagem por e-mail. */
  reply: async (id, answer_text, subject_prefix = 'Resposta — ') =>
    (await api.post(`/admin/contacts/${id}/reply`, { answer_text, subject_prefix })).data,
  /** Remove mensagem (apenas admin master/gestor). */
  remove: async (id) => (await api.delete(`/admin/contacts/${id}`)).data,
};

// ==================== ADMIN DPO EXPORT API ====================
// Exportação completa de dados de um município (LGPD/Governança).
export const adminDpoExportApi = {
  /** Inicia export. Body: { municipio_id?, include_cpf_plain? } */
  create: async (data) => (await api.post('/admin/dpo/exports', data)).data,
  /** Lista exports. */
  list: async () => (await api.get('/admin/dpo/exports')).data,
  /** Status de um export. */
  get: async (id) => (await api.get(`/admin/dpo/exports/${id}`)).data,
  /** URL absoluta de download (usar em <a href>). */
  downloadUrl: (id) => `${BACKEND_URL}/api/admin/dpo/exports/${id}/download`,
  /** Remove export (e o arquivo). */
  remove: async (id) => (await api.delete(`/admin/dpo/exports/${id}`)).data,
};

// ==================== ADMIN PROHIBITED WORDS API ====================
// Gerenciamento dinâmico do filtro de palavras proibidas (sem deploy).
export const adminProhibitedWordsApi = {
  /** Lista palavras. params: { severity?, category?, is_active?, search?, page?, limit? } */
  list: async (params = {}) => (await api.get('/admin/prohibited-words', { params })).data,
  /** Contagens (total, ativas, por severidade). */
  stats: async () => (await api.get('/admin/prohibited-words/stats')).data,
  /** Cria nova palavra. */
  create: async (data) => (await api.post('/admin/prohibited-words', data)).data,
  /** Atualiza palavra existente. */
  update: async (id, data) => (await api.put(`/admin/prohibited-words/${id}`, data)).data,
  /** Remove palavra. */
  remove: async (id) => (await api.delete(`/admin/prohibited-words/${id}`)).data,
  /** Testa um texto contra o filtro ao vivo (para validar regras). */
  test: async (text, context = 'comment') =>
    (await api.post('/admin/prohibited-words/test', { text, context })).data,
  /** Popula a lista com as palavras padrão (idempotente). */
  seed: async (overwrite = false) =>
    (await api.post(`/admin/prohibited-words/seed?overwrite=${overwrite}`)).data,
  /** URL absoluta para download CSV (com filtros opcionais via query). */
  exportUrl: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.severity) qs.set('severity', params.severity);
    if (params.category) qs.set('category', params.category);
    if (params.is_active !== '' && params.is_active != null) qs.set('is_active', params.is_active);
    const query = qs.toString();
    return `${BACKEND_URL}/api/admin/prohibited-words/export${query ? `?${query}` : ''}`;
  },
  /** Importa palavras de um arquivo CSV (FormData com 'file'). */
  importCsv: async (file, overwrite = false) => {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post(`/admin/prohibited-words/import?overwrite=${overwrite}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};

// ==================== CURADORIA DE TAGS (adm_master) ====================

export const adminCuradoriaTagsApi = {
  /** Lista fila de tags livres pendentes com agregação, filtros e paginação. */
  listPendentes: async (params = {}) =>
    (await api.get('/admin/tags/livres-pendentes', { params })).data,

  /** Promove um termo livre para tag controlada + migra negócios em massa. */
  promover: async ({ termo_livre, novo_nome, tipo, subcategoria_ids, categoria_ids }) =>
    (await api.post('/admin/tags/livres-pendentes/promover', {
      termo_livre, novo_nome, tipo, subcategoria_ids, categoria_ids,
    })).data,

  /** Mapeia termo livre como sinônimo de uma tag existente + migra negócios. */
  mapear: async ({ termo_livre, tag_id_destino }) =>
    (await api.post('/admin/tags/livres-pendentes/mapear', {
      termo_livre, tag_id_destino,
    })).data,

  /** Descarta um termo livre — remove da fila sem promover nem mapear. */
  descartar: async ({ termo_livre, motivo }) =>
    (await api.post('/admin/tags/livres-pendentes/descartar', {
      termo_livre, motivo,
    })).data,

  /** Histórico de curadoria (auditoria). */
  log: async (params = {}) =>
    (await api.get('/admin/tags/curadoria/log', { params })).data,

  /** Recalcula contagem_uso em batch (job manual). */
  recount: async () => (await api.post('/admin/tags/recount')).data,

  /** Busca tags para o modal de "mapear como sinônimo". */
  buscarTags: async (params = {}) => (await api.get('/tags/buscar', { params })).data,
};

// ==================== BANNERS ADMIN API (V2) ====================

export const bannersAdminApi = {
  /**
   * List all banners
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/banners', { params });
    return response.data;
  },

  /**
   * Create banner
   */
  create: async (data) => {
    const response = await api.post('/v2/admin/banners', data);
    return response.data;
  },

  /**
   * Update banner
   */
  update: async (bannerId, data) => {
    const response = await api.put(`/v2/admin/banners/${bannerId}`, data);
    return response.data;
  },

  /**
   * Delete banner
   */
  delete: async (bannerId) => {
    const response = await api.delete(`/v2/admin/banners/${bannerId}`);
    return response.data;
  },

  /**
   * Toggle banner status
   */
  toggle: async (bannerId) => {
    const response = await api.patch(`/v2/admin/banners/${bannerId}/toggle`);
    return response.data;
  },

  /**
   * Reorder banners
   * @param {Array<{id: string, ordem: number}>} order
   */
  reorder: async (order) => {
    const response = await api.patch('/v2/admin/banners/reorder', order);
    return response.data;
  },
};

// ==================== HERO BANNERS API (banner principal rotativo) ====================

export const heroBannersApi = {
  /**
   * Público — lista banners ativos para município (fallback global). Aceita
   * `municipio_id` OU `municipio_slug`. Retorna array ordenado por item_order.
   */
  publicList: async (params = {}) => {
    const response = await api.get('/hero-banners', { params });
    return response.data;
  },
  /** Admin — lista todos com filtros opcionais { scope, municipio_id }. */
  adminList: async (params = {}) => {
    const response = await api.get('/admin/hero-banners', { params });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/admin/hero-banners', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/admin/hero-banners/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/admin/hero-banners/${id}`);
    return response.data;
  },
};

// ==================== BANNER ANALYTICS (tracking compartilhado) ====================

export const bannerAnalyticsApi = {
  /**
   * Envia batch de eventos (view/click). Sem auth. Silencioso em falha.
   * events: [{banner_id, banner_type: 'hero'|'promo', event: 'view'|'click', session_id?, municipio_id?}]
   */
  track: async (events) => {
    if (!events || events.length === 0) return { ok: true };
    try {
      const response = await api.post('/banner-analytics/track', { events });
      return response.data;
    } catch (_e) {
      return { ok: false };
    }
  },
  /** Admin — resumo (views/clicks/CTR) por banner. */
  summary: async (params) => {
    const response = await api.get('/admin/banner-analytics/summary', { params });
    return response.data;
  },
  /** Admin — série diária para gráficos. */
  timeseries: async (params) => {
    const response = await api.get('/admin/banner-analytics/timeseries', { params });
    return response.data;
  },
};

// ==================== PARTNERS ADMIN API (V2) ====================

export const partnersAdminApi = {
  /**
   * List all partners
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/partners', { params });
    return response.data;
  },

  /**
   * Create partner
   */
  create: async (data) => {
    const response = await api.post('/v2/admin/partners', data);
    return response.data;
  },

  /**
   * Update partner
   */
  update: async (partnerId, data) => {
    const response = await api.put(`/v2/admin/partners/${partnerId}`, data);
    return response.data;
  },

  /**
   * Delete partner
   */
  delete: async (partnerId) => {
    const response = await api.delete(`/v2/admin/partners/${partnerId}`);
    return response.data;
  },

  /**
   * Toggle partner status
   */
  toggle: async (partnerId) => {
    const response = await api.patch(`/v2/admin/partners/${partnerId}/toggle`);
    return response.data;
  },
};

// ==================== REPORTS ADMIN API ====================

export const reportsAdminApi = {
  /**
   * List reports/denuncias (V2 - tenant-aware)
   */
  list: async (status, entityType) => {
    const params = {};
    if (status) params.status = status;
    if (entityType) params.entity_type = entityType;
    const response = await api.get('/v2/reports/admin', { params });
    return response.data;
  },

  /**
   * Get report details (V2 - tenant-aware)
   */
  get: async (reportId) => {
    const response = await api.get(`/v2/reports/admin/${reportId}`);
    return response.data;
  },

  /**
   * Review report (V2 - tenant-aware)
   */
  review: async (reportId, action, adminNotes) => {
    const params = { action };
    if (adminNotes) params.admin_notes = adminNotes;
    const response = await api.patch(`/v2/reports/admin/${reportId}/review`, null, { params });
    return response.data;
  },

  /**
   * Dismiss all reports for a business (V2 - tenant-aware)
   */
  dismissAllBusiness: async (businessId) => {
    const response = await api.patch(`/v2/reports/admin/business/${businessId}/dismiss-all`);
    return response.data;
  },

  /**
   * Delete report (V2 - tenant-aware)
   */
  delete: async (reportId) => {
    const response = await api.delete(`/v2/reports/admin/${reportId}`);
    return response.data;
  },

  /**
   * Move report to trash (soft delete)
   */
  exclude: async (reportId) => {
    const response = await api.patch(`/v2/reports/admin/${reportId}/exclude`);
    return response.data;
  },

  /**
   * Restore report from trash
   */
  restore: async (reportId) => {
    const response = await api.post(`/v2/reports/admin/${reportId}/restore`);
    return response.data;
  },

  /**
   * Empty the trash — permanently deletes all excluded reports in the tenant
   */
  purgeExcluded: async () => {
    const response = await api.post('/v2/reports/admin/excluded/purge');
    return response.data;
  },

  /**
   * Get report reasons for entity type
   */
  getReasons: async (entityType) => {
    const response = await api.get(`/v2/reports/reasons/${entityType}`);
    return response.data;
  },
};

// ==================== MODERATION ADMIN API ====================

export const moderationAdminApi = {
  /**
   * Get pending businesses
   */
  pendingBusinesses: async () => {
    const response = await api.get('/v2/businesses/admin/pending');
    return response.data;
  },

  /**
   * Approve business
   */
  approveBusiness: async (businessId) => {
    const response = await api.post(`/v2/businesses/admin/${businessId}/approve`);
    return response.data;
  },

  /**
   * Reject business
   */
  rejectBusiness: async (businessId, motivo) => {
    const response = await api.post(`/v2/businesses/admin/${businessId}/reject`, null, {
      params: { motivo }
    });
    return response.data;
  },

  /**
   * Get moderation stats (V2 - tenant-aware)
   */
  stats: async () => {
    const response = await api.get('/v2/admin/moderation/stats');
    return response.data;
  },

  /**
   * Moderate business (V2 - tenant-aware)
   * Actions: warning, suspend, block, unblock
   */
  moderateBusiness: async (businessId, action, reason, durationDays, reportId) => {
    const response = await api.post(`/v2/admin/moderation/business/${businessId}/action`, {
      action,
      reason,
      duration_days: durationDays,
      report_id: reportId
    });
    return response.data;
  },

  /**
   * Moderate user (V2 - tenant-aware)
   * Actions: warning, suspend, block, unblock
   */
  moderateUser: async (userId, action, reason, durationDays, reportId) => {
    const response = await api.post(`/v2/admin/moderation/user/${userId}/action`, {
      action,
      reason,
      duration_days: durationDays,
      report_id: reportId
    });
    return response.data;
  },

  /**
   * Moderate comment (V2 - tenant-aware)
   * Actions: remove_content, warning
   */
  moderateComment: async (commentId, action, reason, reportId) => {
    const response = await api.post(`/v2/admin/moderation/comment/${commentId}/action`, {
      action,
      reason,
      report_id: reportId
    });
    return response.data;
  },

  /**
   * Get moderation history (V2 - tenant-aware)
   */
  getHistory: async (targetType, targetId) => {
    const response = await api.get(`/v2/admin/moderation/history/${targetType}/${targetId}`);
    return response.data;
  },

  // Legacy methods for backwards compatibility
  blockBusiness: async (businessId, reason, duration) => {
    const response = await api.post(`/v2/admin/moderation/business/${businessId}/action`, {
      action: 'block',
      reason,
      duration_days: duration
    });
    return response.data;
  },

  unblockBusiness: async (businessId) => {
    const response = await api.post(`/v2/admin/moderation/business/${businessId}/action`, {
      action: 'unblock',
      reason: 'Desbloqueio solicitado'
    });
    return response.data;
  },

  warnBusiness: async (businessId, reason) => {
    const response = await api.post(`/v2/admin/moderation/business/${businessId}/action`, {
      action: 'warning',
      reason
    });
    return response.data;
  },
};

// ==================== CLAIMS/DISPUTES ADMIN API (V2) ====================

export const claimsAdminApi = {
  /**
   * List claims/disputes (V2 - tenant-aware)
   */
  list: async (status) => {
    const params = status ? { status } : {};
    const response = await api.get('/v2/admin/claims', { params });
    return response.data;
  },

  /**
   * Get claim details (V2 - tenant-aware)
   */
  get: async (claimId) => {
    const response = await api.get(`/v2/admin/claims/${claimId}`);
    return response.data;
  },

  /**
   * Approve claim (V2 - tenant-aware)
   */
  approve: async (claimId) => {
    const response = await api.post(`/v2/admin/claims/${claimId}/approve`);
    return response.data;
  },

  /**
   * Reject claim (V2 - tenant-aware)
   */
  reject: async (claimId, reason) => {
    const response = await api.post(`/v2/admin/claims/${claimId}/reject`, null, {
      params: { reason }
    });
    return response.data;
  },

  /**
   * Get claims statistics (V2 - tenant-aware)
   */
  stats: async () => {
    const response = await api.get('/v2/admin/claims/stats/overview');
    return response.data;
  },
};

// ==================== MUNICIPIOS ADMIN API (V2) ====================

export const municipiosAdminApi = {
  /**
   * List all municipios (admin view) (V2 - tenant-aware)
   */
  list: async (ativo) => {
    const params = ativo !== undefined ? { ativo } : {};
    const response = await api.get('/v2/admin/municipios', { params });
    return response.data;
  },

  /**
   * Create municipio (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/admin/municipios', data);
    invalidateApiCache('municipios:');
    return response.data;
  },

  /**
   * Update municipio (V2 - tenant-aware)
   */
  update: async (municipioId, data) => {
    const response = await api.put(`/v2/admin/municipios/${municipioId}`, data);
    invalidateApiCache('municipios:');
    return response.data;
  },

  /**
   * Get municipio stats (V2 - tenant-aware)
   */
  stats: async (municipioId) => {
    const response = await api.get(`/v2/admin/municipios/${municipioId}/stats`);
    return response.data;
  },
};

// ==================== NOTICIAS ADMIN API (V2) ====================

export const noticiasAdminApi = {
  /**
   * List all news (admin view)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/noticias', { params });
    return response.data;
  },

  /**
   * Get news by ID
   */
  get: async (noticiaId) => {
    const response = await api.get(`/v2/admin/noticias/${noticiaId}`);
    return response.data;
  },

  /**
   * Create news
   */
  create: async (data) => {
    const response = await api.post('/v2/admin/noticias', data);
    invalidateApiCache('noticias:');
    return response.data;
  },

  /**
   * Update news
   */
  update: async (noticiaId, data) => {
    const response = await api.put(`/v2/admin/noticias/${noticiaId}`, data);
    invalidateApiCache('noticias:');
    return response.data;
  },

  /**
   * Delete news
   */
  delete: async (noticiaId) => {
    const response = await api.delete(`/v2/admin/noticias/${noticiaId}`);
    invalidateApiCache('noticias:');
    return response.data;
  },

  /**
   * Toggle news status
   */
  toggle: async (noticiaId) => {
    const response = await api.patch(`/v2/admin/noticias/${noticiaId}/toggle`);
    invalidateApiCache('noticias:');
    return response.data;
  },
};

// ==================== EMAIL ADMIN API (V2) ====================

export const emailAdminApi = {
  /**
   * List email history (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/emails', { params });
    return response.data;
  },

  /**
   * Get email details (V2 - tenant-aware)
   */
  get: async (emailId) => {
    const response = await api.get(`/v2/admin/emails/${emailId}`);
    return response.data;
  },

  /**
   * Send email (V2 - tenant-aware)
   */
  send: async (data) => {
    const response = await api.post('/v2/admin/emails/send', data);
    return response.data;
  },

  /**
   * Send bulk email (V2 - tenant-aware)
   */
  sendBulk: async (data) => {
    const response = await api.post('/v2/admin/emails/send-bulk', data);
    return response.data;
  },

  /**
   * Get email templates (V2 - tenant-aware)
   */
  templates: async () => {
    const response = await api.get('/v2/admin/emails/templates');
    return response.data;
  },

  /**
   * Get email stats (V2 - tenant-aware)
   */
  stats: async () => {
    const response = await api.get('/v2/admin/emails/stats/overview');
    return response.data;
  },

  /**
   * Delete email record (V2 - tenant-aware)
   */
  delete: async (emailId) => {
    const response = await api.delete(`/v2/admin/emails/${emailId}`);
    return response.data;
  },

  /**
   * Retry failed email (V2 - tenant-aware)
   */
  retry: async (emailId) => {
    const response = await api.post(`/v2/admin/emails/${emailId}/retry`);
    return response.data;
  },
};

// ==================== TERRITORIES ADMIN API ====================

export const territoriesAdminApi = {
  /**
   * List territories
   */
  list: async (params = {}) => {
    const response = await api.get('/territories', { params });
    return response.data;
  },

  /**
   * Create territory
   */
  create: async (data) => {
    const response = await api.post('/territories', data);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Update territory
   */
  update: async (territoryId, data) => {
    const response = await api.put(`/territories/${territoryId}`, data);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Delete territory
   */
  delete: async (territoryId) => {
    const response = await api.delete(`/territories/${territoryId}`);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Get territory stats
   */
  stats: async (municipioSlug, extraParams = {}) => {
    const params = { ...(municipioSlug ? { municipio_slug: municipioSlug } : {}), ...extraParams };
    const response = await api.get('/v2/territories/stats', { params });
    return response.data;
  },
};

// ==================== CONFIG ADMIN API ====================

export const configAdminApi = {
  /**
   * Get site config (tenant-aware via query param)
   */
  get: async () => {
    const params = {};
    const saved = localStorage.getItem('admin_selected_municipio');
    if (saved && saved !== 'null' && saved !== 'global') {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.id) params.municipio_id = parsed.id;
      } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
    }
    const response = await api.get('/admin/configuracoes', { params });
    return response.data;
  },

  /**
   * Update site config (tenant-aware via query param)
   */
  update: async (data) => {
    const params = {};
    const saved = localStorage.getItem('admin_selected_municipio');
    if (saved && saved !== 'null' && saved !== 'global') {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.id) params.municipio_id = parsed.id;
      } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
    }
    const response = await api.put('/admin/configuracoes', data, { params });
    return response.data;
  },

  /**
   * Delete custom config for current municipality, revert to global
   */
  deleteCustom: async () => {
    const params = {};
    const saved = localStorage.getItem('admin_selected_municipio');
    if (saved && saved !== 'null' && saved !== 'global') {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.id) params.municipio_id = parsed.id;
      } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
    }
    const response = await api.delete('/admin/configuracoes/custom', { params });
    return response.data;
  },
};

// ==================== CONTACT ADMIN API ====================

export const contactAdminApi = {
  /**
   * List contacts
   */
  list: async (status) => {
    const params = status ? { status } : {};
    const response = await api.get('/admin/contacts', { params });
    return response.data;
  },

  /**
   * Update contact status
   */
  updateStatus: async (contactId, status) => {
    const response = await api.patch(`/admin/contacts/${contactId}`, null, {
      params: { status }
    });
    return response.data;
  },

  /**
   * Delete contact
   */
  delete: async (contactId) => {
    const response = await api.delete(`/admin/contacts/${contactId}`);
    return response.data;
  },
};

// ==================== BAIRROS API ====================

export const bairrosApi = {
  /**
   * List bairros (neighborhoods) for a municipality
   */
  list: async (municipioSlug) => cachedGet(
    `bairros:list:${municipioSlug || 'all'}`,
    async () => {
      const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
      const response = await api.get('/bairros', { params });
      return response.data;
    },
  ),
};

// ==================== NOTICIAS API ====================

export const noticiasApi = {
  /**
   * List news
   */
  list: async (params = {}) => {
    const tenantId = params.municipio_slug || (params.municipio_id ? null : getTenantId());
    const queryParams = { ...params };
    if (tenantId && !queryParams.municipio_slug) {
      queryParams.municipio_id = tenantId;
    }
    // Cache key includes the full query (deterministic) so different filters don't collide.
    const cacheKey = `noticias:list:${_stableKey(queryParams)}`;
    return cachedGet(cacheKey, async () => {
      const response = await api.get('/noticias', { params: queryParams });
      return response.data;
    });
  },

  /**
   * Get single news by slug
   */
  get: async (slug) => cachedGet(`noticias:detail:${slug}`, async () => {
    const response = await api.get(`/noticias/${slug}`);
    return response.data;
  }),
};

// ==================== PROMOTIONS API ====================

export const promotionsApi = {
  /**
   * List promotions (V2 - tenant-aware)
   */
  list: async (ativo = true) => {
    const response = await api.get('/v2/promotions', { params: { ativo } });
    return response.data;
  },

  /**
   * Get promotions for a business (V2 - tenant-aware)
   */
  getByBusiness: async (businessId, ativo = true) => {
    const response = await api.get(`/v2/promotions/business/${businessId}`, { params: { ativo } });
    return response.data;
  },

  /**
   * Create promotion (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/promotions', data);
    return response.data;
  },

  /**
   * Update promotion (V2 - tenant-aware)
   */
  update: async (promotionId, data) => {
    const response = await api.put(`/v2/promotions/${promotionId}`, data);
    return response.data;
  },

  /**
   * Delete promotion (V2 - tenant-aware)
   */
  delete: async (promotionId) => {
    const response = await api.delete(`/v2/promotions/${promotionId}`);
    return response.data;
  },

  /**
   * Toggle promotion status (V2 - tenant-aware)
   */
  toggle: async (promotionId) => {
    const response = await api.patch(`/v2/promotions/${promotionId}/toggle`);
    return response.data;
  },

  /**
   * Get my promotions (V2 - tenant-aware)
   */
  my: async () => {
    const response = await api.get('/v2/promotions/my');
    return response.data;
  },
};

// ==================== JOBS API ====================

export const jobsApi = {
  /**
   * List jobs (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/jobs', { params });
    return response.data;
  },

  /**
   * List public jobs (V2 - tenant-aware)
   */
  listPublic: async (params = {}) => {
    const response = await api.get('/v2/jobs', { params: { ...params, status: 'active' } });
    return response.data;
  },

  /**
   * Count jobs (V2 - tenant-aware)
   */
  count: async (status = 'active') => {
    const response = await api.get('/v2/jobs/count', { params: { status } });
    return response.data;
  },

  /**
   * Get job details (V2 - tenant-aware)
   */
  get: async (jobId) => {
    const response = await api.get(`/v2/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Get jobs for a business (V2 - tenant-aware)
   */
  getByBusiness: async (businessId, status = 'all') => {
    const response = await api.get(`/v2/jobs/business/${businessId}`, { params: { status } });
    return response.data;
  },

  /**
   * Create job (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/jobs', data);
    return response.data;
  },

  /**
   * Update job (V2 - tenant-aware)
   */
  update: async (jobId, data) => {
    const response = await api.put(`/v2/jobs/${jobId}`, data);
    return response.data;
  },

  /**
   * Close job with feedback (V2 - tenant-aware)
   */
  close: async (jobId, data) => {
    const response = await api.post(`/v2/jobs/${jobId}/close`, data);
    return response.data;
  },

  /**
   * Delete job (V2 - tenant-aware)
   */
  delete: async (jobId) => {
    const response = await api.delete(`/v2/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Track job view (V2 - tenant-aware)
   */
  trackView: async (jobId) => {
    const response = await api.post(`/v2/jobs/${jobId}/track-view`);
    return response.data;
  },

  /**
   * Track job click (V2 - tenant-aware)
   */
  trackClick: async (jobId, channel) => {
    const response = await api.post(`/v2/jobs/${jobId}/track-click`, null, {
      params: { channel }
    });
    return response.data;
  },

  /**
   * Get my jobs (V2 - tenant-aware)
   */
  my: async (status = 'all') => {
    const response = await api.get('/v2/jobs/my', { params: { status } });
    return response.data;
  },
};

// ==================== SEARCH API (V2) ====================

export const searchApi = {
  /**
   * Unified search (V2 - tenant-aware)
   */
  search: async (q, params = {}) => {
    const response = await api.get('/v2/search', { params: { q, ...params } });
    return response.data;
  },

  /**
   * Get search suggestions (V2 - tenant-aware)
   */
  suggestions: async (q, limit = 10) => {
    const response = await api.get('/v2/search/suggestions', { params: { q, limit } });
    return response.data;
  },

  /**
   * Log search (V2 - tenant-aware)
   */
  log: async (query, resultsCount, clickedBusinessId = null, filters = null) => {
    const response = await api.post('/v2/search/log', {
      query,
      results_count: resultsCount,
      clicked_business_id: clickedBusinessId,
      filters
    });
    return response.data;
  },

  /**
   * Get search trends (admin) (V2 - tenant-aware)
   */
  trends: async (days = 30) => {
    const response = await api.get('/v2/search/analytics/trends', { params: { days } });
    return response.data;
  },

  /**
   * Get business search analytics (V2 - tenant-aware)
   */
  businessAnalytics: async (businessId, days = 30) => {
    const response = await api.get(`/v2/search/analytics/business/${businessId}`, { params: { days } });
    return response.data;
  },
};

// ==================== SOCIAL/ORG PROJECTS API ====================

export const socialApi = {
  /**
   * Get featured organizations
   */
  featured: async (municipioSlug, limit = 4) => {
    const params = { limit };
    if (municipioSlug) params.municipio_slug = municipioSlug;
    const response = await api.get('/org-projects/featured', { params });
    return response.data;
  },

  /**
   * Get upcoming activities
   */
  upcomingActivities: async (municipioSlug, limit = 4) => {
    const params = { limit, upcoming: true };
    if (municipioSlug) params.municipio_slug = municipioSlug;
    const response = await api.get('/org-activities/upcoming', { params });
    return response.data;
  },

  /**
   * List organizations
   */
  list: async (params = {}) => {
    const response = await api.get('/org-projects', { params });
    return response.data;
  },

  /**
   * Get single organization
   */
  get: async (orgId) => {
    const response = await api.get(`/org-projects/${orgId}`);
    return response.data;
  },
};

// ==================== ANALYTICS API ====================

export const analyticsApi = {
  /**
   * Track business view
   */
  trackView: async (businessId) => {
    const response = await api.post(`/analytics/view/${businessId}`);
    return response.data;
  },

  /**
   * Track WhatsApp click
   */
  trackWhatsappClick: async (businessId) => {
    const response = await api.post(`/analytics/whatsapp-click/${businessId}`);
    return response.data;
  },

  /**
   * Track share
   */
  trackShare: async (businessId, platform) => {
    const response = await api.post(`/analytics/share/${businessId}`, null, {
      params: { platform }
    });
    return response.data;
  },

  /**
   * Get dashboard (authenticated)
   */
  dashboard: async () => {
    const response = await api.get('/analytics/dashboard');
    return response.data;
  },

  /**
   * Get quality analytics (V2 - tenant-aware)
   */
  qualidade: async () => {
    const response = await api.get('/v2/analytics/qualidade');
    return response.data;
  },

  /**
   * Get demand analytics (V2 - tenant-aware)
   */
  demanda: async (periodo = '7') => {
    const response = await api.get('/v2/analytics/demanda', { params: { periodo } });
    return response.data;
  },

  /**
   * Get empreendedor indicators (V2 - tenant-aware)
   */
  empreendedorIndicators: async () => {
    const response = await api.get('/v2/analytics/empreendedor-indicators');
    return response.data;
  },

  /**
   * Get relatorios data (V2 - tenant-aware)
   */
  relatorios: async () => {
    const response = await api.get('/v2/analytics/relatorios');
    return response.data;
  },
};

// ==================== REVIEWS API ====================

export const reviewsApi = {
  /**
   * Get reviews for a business
   */
  list: async (businessId) => {
    const response = await api.get(`/reviews/${businessId}`);
    return response.data;
  },

  /**
   * Get rating stats for a business
   */
  stats: async (businessId) => {
    const response = await api.get(`/reviews/${businessId}/stats`);
    return response.data;
  },

  /**
   * Create a review
   */
  create: async (data) => {
    const response = await api.post('/reviews', data);
    return response.data;
  },
};

// ==================== FAVORITES API ====================

export const favoritesApi = {
  /**
   * Get favorites (V2 - tenant-aware)
   */
  list: async () => {
    const response = await api.get('/v2/favorites');
    return response.data;
  },

  /**
   * Add favorite (V2 - tenant-aware)
   */
  add: async (businessId) => {
    const response = await api.post(`/v2/favorites/${businessId}`);
    return response.data;
  },

  /**
   * Remove favorite (V2 - tenant-aware)
   */
  remove: async (businessId) => {
    const response = await api.delete(`/v2/favorites/${businessId}`);
    return response.data;
  },

  /**
   * Check if business is favorited (V2 - tenant-aware)
   */
  check: async (businessId) => {
    const response = await api.get(`/v2/favorites/check/${businessId}`);
    return response.data;
  },

  /**
   * Get favorites count (V2 - tenant-aware)
   */
  count: async () => {
    const response = await api.get('/v2/favorites/count');
    return response.data;
  },

  /**
   * Get business favorites count (V2 - tenant-aware)
   */
  businessCount: async (businessId) => {
    const response = await api.get(`/v2/favorites/business/${businessId}/count`);
    return response.data;
  },
};

// ==================== COMMENTS API (V2) ====================

export const commentsApi = {
  /**
   * Get comments for a business (V2 - tenant-aware)
   */
  getByBusiness: async (businessId, params = {}) => {
    const response = await api.get(`/v2/comments/business/${businessId}`, { params });
    return response.data;
  },

  /**
   * Create comment on a business (V2 - tenant-aware)
   */
  create: async (businessId, data) => {
    const response = await api.post(`/v2/comments/business/${businessId}`, data);
    return response.data;
  },

  /**
   * Add owner reply (V2 - tenant-aware)
   */
  addReply: async (commentId, content) => {
    const response = await api.post(`/v2/comments/${commentId}/reply`, { content });
    return response.data;
  },

  /**
   * Edit owner reply (V2 - tenant-aware)
   */
  editReply: async (commentId, content) => {
    const response = await api.put(`/v2/comments/${commentId}/reply`, { content });
    return response.data;
  },

  /**
   * Delete owner reply (V2 - tenant-aware)
   */
  deleteReply: async (commentId) => {
    const response = await api.delete(`/v2/comments/${commentId}/reply`);
    return response.data;
  },

  /**
   * Like comment (V2 - tenant-aware)
   */
  like: async (commentId) => {
    const response = await api.post(`/v2/comments/${commentId}/like`);
    return response.data;
  },

  /**
   * Unlike comment (V2 - tenant-aware)
   */
  unlike: async (commentId) => {
    const response = await api.delete(`/v2/comments/${commentId}/like`);
    return response.data;
  },

  /**
   * Delete comment (V2 - tenant-aware)
   */
  delete: async (commentId) => {
    const response = await api.delete(`/v2/comments/${commentId}`);
    return response.data;
  },

  /**
   * Get my comments (V2 - tenant-aware)
   */
  my: async () => {
    const response = await api.get('/v2/comments/my');
    return response.data;
  },
};

// ==================== NOTIFICATIONS API (V2) ====================

export const notificationsApi = {
  /**
   * List notifications (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/notifications', { params });
    return response.data;
  },

  /**
   * Get unread count (V2 - tenant-aware)
   */
  unreadCount: async () => {
    const response = await api.get('/v2/notifications/unread-count');
    return response.data;
  },

  /**
   * Get notification types distribution (V2 - tenant-aware)
   */
  types: async () => {
    const response = await api.get('/v2/notifications/types');
    return response.data;
  },

  /**
   * Mark notification as read (V2 - tenant-aware)
   */
  markRead: async (notificationId) => {
    const response = await api.patch(`/v2/notifications/${notificationId}/read`);
    return response.data;
  },

  /**
   * Mark all as read (V2 - tenant-aware)
   */
  markAllRead: async (params = {}) => {
    const response = await api.post('/v2/notifications/mark-all-read', null, { params });
    return response.data;
  },

  /**
   * Delete notification (V2 - tenant-aware)
   */
  delete: async (notificationId) => {
    const response = await api.delete(`/v2/notifications/${notificationId}`);
    return response.data;
  },

  /**
   * Delete all notifications (V2 - tenant-aware)
   */
  deleteAll: async (params = {}) => {
    const response = await api.delete('/v2/notifications', { params });
    return response.data;
  },

  /**
   * Get notification settings (V2 - tenant-aware)
   */
  getSettings: async () => {
    const response = await api.get('/v2/notifications/settings');
    return response.data;
  },

  /**
   * Update notification settings (V2 - tenant-aware)
   */
  updateSettings: async (settings) => {
    const response = await api.put('/v2/notifications/settings', settings);
    return response.data;
  },
};

// ==================== MUNICIPIOS API ====================

export const municipiosApi = {
  /**
   * List municipios
   */
  list: async () => cachedGet('municipios:list', async () => {
    const response = await api.get('/municipios');
    return response.data;
  }),

  /**
   * Get nearest municipio
   */
  nearest: async (lat, lng) => {
    const response = await api.get('/municipios/nearest', { params: { lat, lng } });
    return response.data;
  },

  /**
   * Get municipio by slug
   */
  get: async (slug) => {
    const response = await api.get(`/municipios/${slug}`);
    return response.data;
  },

  // ===== Custom Domains (white-label) =====
  listDomains: async (municipio_id) => {
    const response = await api.get(`/admin/municipios/${municipio_id}/domains`);
    return response.data;
  },
  addDomain: async (municipio_id, domain) => {
    const response = await api.post(`/admin/municipios/${municipio_id}/domains`, { domain });
    return response.data;
  },
  removeDomain: async (municipio_id, domain) => {
    const response = await api.delete(`/admin/municipios/${municipio_id}/domains/${encodeURIComponent(domain)}`);
    return response.data;
  },
  setPrimaryDomain: async (municipio_id, domain) => {
    const response = await api.put(`/admin/municipios/${municipio_id}/domains/primary`, { domain });
    return response.data;
  },
  verifyDomain: async (municipio_id, domain) => {
    const response = await api.get(`/admin/municipios/${municipio_id}/domains/${encodeURIComponent(domain)}/verify`);
    return response.data;
  },
};

// ==================== TERRITORIES API ====================

export const territoriesApi = {
  /**
   * List territories
   */
  list: async (params = {}) => cachedGet(
    `territories:list:${_stableKey(params)}`,
    async () => {
      const response = await api.get('/territories', { params });
      return response.data;
    },
  ),

  /**
   * Get districts for a municipality
   */
  distritos: async (municipioSlug) => cachedGet(
    `territories:distritos:${municipioSlug}`,
    async () => {
      const response = await api.get(`/territories/distritos/${municipioSlug}`);
      return response.data;
    },
  ),

  /**
   * Get bairros for a territory
   */
  bairros: async (parentId) => cachedGet(
    `territories:bairros:${parentId}`,
    async () => {
      const response = await api.get(`/territories/bairros/${parentId}`);
      return response.data;
    },
  ),
};

// ==================== DASHBOARD API ====================

export const dashboardApi = {
  /**
   * Get public dashboard KPIs
   */
  public: async (municipioSlug) => {
    const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
    const response = await api.get('/v2/dashboard/public', { params });
    return response.data;
  },

  /**
   * Get admin dashboard KPIs (requires auth). `periodDays` applies to
   * time-bounded metrics (nota média recente, tendência de contatos).
   */
  admin: async (territoryId, periodDays, extra = {}) => {
    const params = {};
    if (territoryId) params.territory_id = territoryId;
    if (periodDays) params.period_days = periodDays;
    if (extra.bairro) params.bairro = extra.bairro;
    if (extra.categoria) params.categoria = extra.categoria;
    const response = await api.get('/v2/dashboard/admin', { params });
    return response.data;
  },

  /**
   * Get user dashboard (requires auth)
   */
  me: async () => {
    const response = await api.get('/v2/dashboard/me');
    return response.data;
  },

  /**
   * Send session heartbeat (tracks active users)
   */
  heartbeat: async () => {
    try {
      await api.post('/v2/dashboard/session/heartbeat');
    } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
  },

  /**
   * Track an analytics event
   */
  trackEvent: async (event, businessId, metadata) => {
    try {
      await api.post('/v2/dashboard/event', { event, business_id: businessId, metadata });
    } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("[api] swallowed:", err.message); }
  },
};

// ==================== ALERTS ADMIN API ====================

export const alertsAdminApi = {
  /**
   * Get alerts stats
   */
  stats: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/admin/alerts/stats', { params });
    return response.data;
  },

  /**
   * List public alerts
   */
  listPublic: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/admin/public-alerts', { params });
    return response.data;
  },

  /**
   * Create public alert
   */
  createPublic: async (data) => {
    const response = await api.post('/admin/public-alerts', data);
    return response.data;
  },

  /**
   * Update public alert
   */
  updatePublic: async (alertId, data) => {
    const response = await api.put(`/admin/public-alerts/${alertId}`, data);
    return response.data;
  },

  /**
   * Delete public alert
   */
  deletePublic: async (alertId) => {
    const response = await api.delete(`/admin/public-alerts/${alertId}`);
    return response.data;
  },

  /**
   * Toggle public alert
   */
  togglePublic: async (alertId) => {
    const response = await api.patch(`/admin/public-alerts/${alertId}/toggle`);
    return response.data;
  },

  /**
   * Get subscriptions
   */
  subscriptions: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/admin/alert-subscriptions', { params });
    return response.data;
  },

  /**
   * Send bulk alerts
   */
  sendBulk: async (data) => {
    const response = await api.post('/admin/alerts/send-bulk', data);
    return response.data;
  },

  /**
   * Get bulk alert preview
   */
  previewBulk: async (data) => {
    const response = await api.post('/admin/alerts/preview-bulk', data);
    return response.data;
  },

  /**
   * Get user alerts (internal)
   */
  listInternal: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/admin/user-alerts', { params });
    return response.data;
  },
};

// ==================== PROGRAMS ADMIN API ====================

export const programsAdminApi = {
  /**
   * List programs (admin view)
   */
  list: async (params = {}) => {
    const response = await api.get('/admin/programs', { params });
    return response.data;
  },

  /**
   * Get program registrations
   */
  registrations: async (programId) => {
    const response = await api.get(`/admin/programs/${programId}/registrations`);
    return response.data;
  },

  /**
   * Batch: registrations counts para varios programas em uma unica query.
   * Evita N+1 no /admin/programas-acoes.
   * @param {string[]} programIds
   * @returns {Promise<{counts: Record<string, number>}>}
   */
  registrationsCounts: async (programIds) => {
    if (!programIds || programIds.length === 0) return { counts: {} };
    const response = await api.get('/admin/programs/registrations-counts', {
      params: { program_ids: programIds.join(',') },
    });
    return response.data;
  },

  /**
   * Export registrations (csv | xlsx | pdf) — returns Blob
   */
  exportRegistrations: async (programId, format = 'csv') => {
    const response = await api.get(`/admin/programs/${programId}/registrations/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ==================== BUSINESSES ADMIN API ====================

export const businessesAdminApi = {
  /**
   * List businesses (admin view)
   */
  list: async (params = {}) => {
    const response = await api.get('/admin/businesses', { params });
    return response.data;
  },

  /**
   * Get business by ID
   */
  get: async (businessId) => {
    const response = await api.get(`/admin/businesses/${businessId}`);
    return response.data;
  },

  /**
   * Approve business
   */
  approve: async (businessId) => {
    const response = await api.patch(`/admin/businesses/${businessId}/approve`);
    return response.data;
  },

  /**
   * Reject business
   */
  reject: async (businessId, motivo) => {
    const response = await api.patch(`/admin/businesses/${businessId}/reject`, null, {
      params: { motivo }
    });
    return response.data;
  },

  /**
   * Set business as pending
   */
  setPending: async (businessId) => {
    const response = await api.post(`/admin/businesses/${businessId}/pending`);
    return response.data;
  },

  /**
   * Suspend business
   */
  suspend: async (businessId, reason) => {
    const response = await api.patch(`/admin/businesses/${businessId}/suspend`, null, {
      params: { motivo: reason }
    });
    return response.data;
  },

  /**
   * Reopen business
   */
  reopen: async (businessId) => {
    const response = await api.patch(`/admin/businesses/${businessId}/reopen`);
    return response.data;
  },

  /**
   * Update business (admin)
   */
  update: async (businessId, data) => {
    const response = await api.put(`/admin/businesses/${businessId}`, data);
    return response.data;
  },

  /**
   * Delete business (admin) — permanente. Usa endpoint público
   * /api/businesses/{id} que já valida permissão (OWNER ou ADMIN).
   */
  delete: async (businessId) => {
    const response = await api.delete(`/businesses/${businessId}`);
    return response.data;
  },
};

// ==================== NOTIFICATIONS ADMIN API ====================

export const notificationsAdminApi = {
  /**
   * List notifications
   */
  list: async (params = {}) => {
    const response = await api.get('/admin/notifications', { params });
    return response.data;
  },

  /**
   * Send notification
   */
  send: async (data) => {
    const response = await api.post('/admin/notifications/send', data);
    return response.data;
  },

  /**
   * Get notification templates
   */
  templates: async () => {
    const response = await api.get('/admin/notifications/templates');
    return response.data;
  },
};

// ==================== AUDIT LOGS ADMIN API (V2) ====================

export const auditLogsAdminApi = {
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/audit-logs', { params });
    return response.data;
  },
  get: async (logId) => {
    const response = await api.get(`/v2/admin/audit-logs/${logId}`);
    return response.data;
  },
  getActions: async () => {
    const response = await api.get('/v2/admin/audit-logs/actions');
    return response.data;
  },
  getModules: async () => {
    const response = await api.get('/v2/admin/audit-logs/modules');
    return response.data;
  },
  entityTypes: async () => {
    const response = await api.get('/v2/admin/audit-logs/entity-types');
    return response.data;
  },
  getStats: async (days = 30) => {
    const response = await api.get('/v2/admin/audit-logs/stats/overview', { params: { days } });
    return response.data;
  },
  export: async (days = 30, format = 'json') => {
    const response = await api.get('/v2/admin/audit-logs/export', { params: { days, format } });
    return response.data;
  },
};

// ==================== ADMIN SYSTEM ALERTS API ====================

export const systemAlertsApi = {
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/system-alerts', { params });
    return response.data;
  },
  get: async (alertId) => {
    const response = await api.get(`/v2/admin/system-alerts/${alertId}`);
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/v2/admin/system-alerts/unread-count');
    return response.data;
  },
  markRead: async (alertId) => {
    const response = await api.patch(`/v2/admin/system-alerts/${alertId}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/v2/admin/system-alerts/mark-all-read');
    return response.data;
  },
  resolve: async (alertId) => {
    const response = await api.patch(`/v2/admin/system-alerts/${alertId}/resolve`);
    return response.data;
  },
  acknowledge: async (alertId) => {
    const response = await api.patch(`/v2/admin/system-alerts/${alertId}/acknowledge`);
    return response.data;
  },
  getStats: async (days = 30) => {
    const response = await api.get('/v2/admin/system-alerts/stats/summary', { params: { days } });
    return response.data;
  },
};

// ==================== ALERT RULES CONFIG API ====================

export const alertRulesApi = {
  list: async () => {
    const response = await api.get('/v2/admin/alert-rules');
    return response.data;
  },
  update: async (ruleId, data) => {
    const response = await api.patch(`/v2/admin/alert-rules/${ruleId}`, data);
    return response.data;
  },
  reset: async (ruleId) => {
    const response = await api.post(`/v2/admin/alert-rules/${ruleId}/reset`);
    return response.data;
  },
};

// ==================== RBAC API ====================

export const rbacApi = {
  getConfig: async () => {
    const response = await api.get('/v2/admin/rbac/config');
    return response.data;
  },
  getModules: async () => {
    const response = await api.get('/v2/admin/rbac/modules');
    return response.data;
  },
  getRoles: async () => {
    const response = await api.get('/v2/admin/rbac/roles');
    return response.data;
  },
  updateRolePermissions: async (roleId, permissions) => {
    const response = await api.put(`/v2/admin/rbac/config/${roleId}`, { permissions });
    return response.data;
  },
  resetRolePermissions: async (roleId) => {
    const response = await api.post(`/v2/admin/rbac/config/${roleId}/reset`);
    return response.data;
  },
  getUserPermissions: async (email) => {
    const response = await api.get(`/v2/admin/rbac/user-permissions/${email}`);
    return response.data;
  },
};

// ==================== FEATURE FLAGS API ====================

export const featureFlagsApi = {
  getConfig: async () => {
    const response = await api.get('/v2/admin/feature-flags');
    return response.data;
  },
  updateFlag: async (featureId, data) => {
    const response = await api.put(`/v2/admin/feature-flags/${featureId}`, data);
    return response.data;
  },
  resetFlag: async (featureId) => {
    const response = await api.post(`/v2/admin/feature-flags/${featureId}/reset`);
    return response.data;
  },
  resetAll: async () => {
    const response = await api.post('/v2/admin/feature-flags/reset-all');
    return response.data;
  },
  bulkUpdate: async (updates) => {
    const response = await api.put('/v2/admin/feature-flags/bulk', updates);
    return response.data;
  },
  getMyFlags: async () => {
    const response = await api.get('/v2/admin/feature-flags/me');
    return response.data;
  },
  getPublicFlags: async () => {
    const response = await api.get('/v2/admin/feature-flags/public');
    return response.data;
  },
};

// ==================== PASSWORD VALIDATION API ====================

export const passwordApi = {
  validate: async (password) => {
    const response = await api.post('/v2/auth/validate-password', { password });
    return response.data;
  },
};

// ==================== EMAIL TEMPLATES ADMIN API (V2) ====================

export const emailTemplatesAdminApi = {
  /**
   * List email templates (V2 - tenant-aware)
   */
  list: async () => {
    const response = await api.get('/v2/admin/email-templates');
    return response.data;
  },

  /**
   * Get email template by ID (V2 - tenant-aware)
   */
  get: async (templateId) => {
    const response = await api.get(`/v2/admin/email-templates/${templateId}`);
    return response.data;
  },

  /**
   * Update email template (V2 - tenant-aware)
   */
  update: async (templateId, data) => {
    const response = await api.put(`/v2/admin/email-templates/${templateId}`, data);
    return response.data;
  },

  /**
   * Toggle email template status (V2 - tenant-aware)
   */
  toggle: async (templateId) => {
    const response = await api.patch(`/v2/admin/email-templates/${templateId}/toggle`);
    return response.data;
  },

  /**
   * Send test email (V2 - tenant-aware)
   */
  sendTest: async (templateId, email) => {
    const response = await api.post(`/v2/admin/email-templates/${templateId}/test`, null, {
      params: { email }
    });
    return response.data;
  },
};

// ==================== CONTENT MODERATION ADMIN API (V2) ====================

export const contentModerationAdminApi = {
  /**
   * List moderation logs (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/admin/content-moderation/logs', { params });
    return response.data;
  },

  /**
   * Get moderation stats (V2 - tenant-aware)
   */
  stats: async () => {
    const response = await api.get('/v2/admin/content-moderation/stats');
    return response.data;
  },

  /**
   * Get available contexts (V2 - tenant-aware)
   */
  contexts: async () => {
    const response = await api.get('/v2/admin/content-moderation/contexts');
    return response.data;
  },

  /**
   * Review moderation log (V2 - tenant-aware)
   */
  review: async (logId, action, notes) => {
    const params = { action };
    if (notes) params.notes = notes;
    const response = await api.post(`/v2/admin/content-moderation/logs/${logId}/review`, null, { params });
    return response.data;
  },

  /**
   * Get moderation log by ID (V2 - tenant-aware)
   */
  get: async (logId) => {
    const response = await api.get(`/v2/admin/content-moderation/logs/${logId}`);
    return response.data;
  },
};

// ==================== COMMENTS ADMIN MODERATION (V2) ====================

export const commentsAdminModerationApi = {
  listPending: async (limit = 50) => {
    const response = await api.get('/v2/comments/admin/pending', { params: { limit } });
    return response.data;
  },
  approve: async (commentId) => {
    const response = await api.post(`/v2/comments/admin/${commentId}/approve`);
    return response.data;
  },
  reject: async (commentId, reason) => {
    const response = await api.post(`/v2/comments/admin/${commentId}/reject`, null, {
      params: reason ? { reason } : {},
    });
    return response.data;
  },
  exclude: async (commentId) => {
    const response = await api.post(`/v2/comments/admin/${commentId}/exclude`);
    return response.data;
  },
  restore: async (commentId) => {
    const response = await api.post(`/v2/comments/admin/${commentId}/restore`);
    return response.data;
  },
  purgeExcluded: async () => {
    const response = await api.post('/v2/comments/admin/excluded/purge');
    return response.data;
  },
};

// ==================== REVIEWS ADMIN MODERATION (V2) ====================

export const reviewsAdminModerationApi = {
  listPending: async (limit = 50) => {
    const response = await api.get('/v2/reviews/admin/pending', { params: { limit } });
    return response.data;
  },
  approve: async (reviewId) => {
    const response = await api.post(`/v2/reviews/admin/${reviewId}/approve`);
    return response.data;
  },
  reject: async (reviewId, reason) => {
    const response = await api.post(`/v2/reviews/admin/${reviewId}/reject`, null, {
      params: reason ? { reason } : {},
    });
    return response.data;
  },
  exclude: async (reviewId) => {
    const response = await api.post(`/v2/reviews/admin/${reviewId}/exclude`);
    return response.data;
  },
  restore: async (reviewId) => {
    const response = await api.post(`/v2/reviews/admin/${reviewId}/restore`);
    return response.data;
  },
  purgeExcluded: async () => {
    const response = await api.post('/v2/reviews/admin/excluded/purge');
    return response.data;
  },
};

// ==================== ORG-REVIEWS (DEPOIMENTOS) ADMIN MODERATION (iter283) ====================

export const orgReviewsAdminModerationApi = {
  listPending: async (limit = 50) => {
    const response = await api.get('/v2/org-reviews/admin/pending', { params: { limit } });
    return response.data;
  },
  approve: async (reviewId) => {
    const response = await api.post(`/v2/org-reviews/admin/${reviewId}/approve`);
    return response.data;
  },
  reject: async (reviewId, reason) => {
    const response = await api.post(`/v2/org-reviews/admin/${reviewId}/reject`, null, {
      params: reason ? { reason } : {},
    });
    return response.data;
  },
  exclude: async (reviewId) => {
    const response = await api.post(`/v2/org-reviews/admin/${reviewId}/exclude`);
    return response.data;
  },
  restore: async (reviewId) => {
    const response = await api.post(`/v2/org-reviews/admin/${reviewId}/restore`);
    return response.data;
  },
  purgeExcluded: async () => {
    const response = await api.post('/v2/org-reviews/admin/excluded/purge');
    return response.data;
  },
};

// ==================== CONTENT MODERATION LOGS EXTRA ACTIONS (V2) ====================

export const contentModerationExtraApi = {
  exclude: async (logId, notes) => {
    const params = { action: 'exclude' };
    if (notes) params.notes = notes;
    const response = await api.post(`/v2/admin/content-moderation/logs/${logId}/review`, null, { params });
    return response.data;
  },
  restore: async (logId) => {
    const response = await api.post(`/v2/admin/content-moderation/logs/${logId}/restore`);
    return response.data;
  },
  purgeExcluded: async () => {
    const response = await api.post('/v2/admin/content-moderation/excluded/purge');
    return response.data;
  },
};

// ==================== TERRITORIES ADMIN API (V2) ====================

export const territoriesAdminApiV2 = {
  /**
   * List territories (V2 - tenant-aware)
   */
  list: async (params = {}) => {
    const response = await api.get('/v2/territories', { params });
    return response.data;
  },

  /**
   * Get territory by ID (V2 - tenant-aware)
   */
  get: async (territoryId) => {
    const response = await api.get(`/v2/territories/${territoryId}`);
    return response.data;
  },

  /**
   * Create territory (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/territories', data);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Update territory (V2 - tenant-aware)
   */
  update: async (territoryId, data) => {
    const response = await api.patch(`/v2/territories/${territoryId}`, data);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Delete territory (V2 - tenant-aware)
   */
  delete: async (territoryId) => {
    const response = await api.delete(`/v2/territories/${territoryId}`);
    invalidateApiCache('territories:');
    invalidateApiCache('bairros:');
    return response.data;
  },

  /**
   * Get territory stats (V2 - tenant-aware)
   */
  stats: async (municipioSlug) => {
    const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
    const response = await api.get('/v2/territories/stats', { params });
    return response.data;
  },

  /**
   * Get districts for a municipality (V2 - tenant-aware)
   */
  distritos: async (municipioSlug) => {
    const response = await api.get(`/v2/territories/distritos/${municipioSlug}`);
    return response.data;
  },

  /**
   * Get bairros for a parent territory (V2 - tenant-aware)
   */
  bairros: async (parentId) => {
    const response = await api.get(`/v2/territories/bairros/${parentId}`);
    return response.data;
  },
};

// ==================== PAGES ADMIN API (V2) ====================

export const pagesAdminApi = {
  /**
   * Get terms of service (V2 - tenant-aware)
   */
  getTermos: async () => {
    const response = await api.get('/v2/admin/paginas/termos');
    return response.data;
  },

  /**
   * Update terms of service (V2 - tenant-aware)
   */
  updateTermos: async (conteudo) => {
    const response = await api.put('/v2/admin/paginas/termos', { conteudo });
    return response.data;
  },

  /**
   * Get privacy policy (V2 - tenant-aware)
   */
  getLgpd: async () => {
    const response = await api.get('/v2/admin/paginas/lgpd');
    return response.data;
  },

  /**
   * Update privacy policy (V2 - tenant-aware)
   */
  updateLgpd: async (conteudo) => {
    const response = await api.put('/v2/admin/paginas/lgpd', { conteudo });
    return response.data;
  },

  deleteCustomLgpd: async () => {
    const response = await api.delete('/v2/admin/paginas/lgpd/custom');
    return response.data;
  },

  deleteCustomTermos: async () => {
    const response = await api.delete('/v2/admin/paginas/termos/custom');
    return response.data;
  },

  getCookies: async () => {
    const response = await api.get('/v2/admin/paginas/cookies');
    return response.data;
  },

  updateCookies: async (conteudo) => {
    const response = await api.put('/v2/admin/paginas/cookies', { conteudo });
    return response.data;
  },

  deleteCustomCookies: async () => {
    const response = await api.delete('/v2/admin/paginas/cookies/custom');
    return response.data;
  },

  getPublicCookies: async () => {
    const response = await api.get('/v2/admin/paginas/public/cookies');
    return response.data;
  },
};

// ==================== BANNERS API (V2) ====================

export const bannersApiV2 = {
  /**
   * List active banners (V2 - tenant-aware)
   */
  list: async (municipioSlug) => {
    const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
    const response = await api.get('/v2/banners', { params });
    return response.data;
  },

  /**
   * Admin: List all banners (V2 - tenant-aware)
   */
  adminList: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/v2/banners/admin/all', { params });
    return response.data;
  },

  /**
   * Admin: Create banner (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/banners', data);
    return response.data;
  },

  /**
   * Admin: Update banner (V2 - tenant-aware)
   */
  update: async (bannerId, data) => {
    const response = await api.put(`/v2/banners/${bannerId}`, data);
    return response.data;
  },

  /**
   * Admin: Delete banner (V2 - tenant-aware)
   */
  delete: async (bannerId) => {
    const response = await api.delete(`/v2/banners/${bannerId}`);
    return response.data;
  },

  /**
   * Admin: Toggle banner (V2 - tenant-aware)
   */
  toggle: async (bannerId) => {
    const response = await api.patch(`/v2/banners/${bannerId}/toggle`);
    return response.data;
  },

  /**
   * Admin: Reorder banners (V2 - tenant-aware)
   */
  reorder: async (order) => {
    const response = await api.patch('/v2/banners/reorder', order);
    return response.data;
  },
};

// ==================== PARTNERS API (V2) ====================

export const partnersApiV2 = {
  /**
   * List active partners (V2 - tenant-aware)
   */
  list: async (municipioSlug) => {
    const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
    const response = await api.get('/v2/partners', { params });
    return response.data;
  },

  /**
   * Admin: List all partners (V2 - tenant-aware)
   */
  adminList: async (municipioId) => {
    const params = municipioId ? { municipio_id: municipioId } : {};
    const response = await api.get('/v2/partners/admin/all', { params });
    return response.data;
  },

  /**
   * Admin: Create partner (V2 - tenant-aware)
   */
  create: async (data) => {
    const response = await api.post('/v2/partners', data);
    return response.data;
  },

  /**
   * Admin: Update partner (V2 - tenant-aware)
   */
  update: async (partnerId, data) => {
    const response = await api.put(`/v2/partners/${partnerId}`, data);
    return response.data;
  },

  /**
   * Admin: Delete partner (V2 - tenant-aware)
   */
  delete: async (partnerId) => {
    const response = await api.delete(`/v2/partners/${partnerId}`);
    return response.data;
  },

  /**
   * Admin: Toggle partner (V2 - tenant-aware)
   */
  toggle: async (partnerId) => {
    const response = await api.patch(`/v2/partners/${partnerId}/toggle`);
    return response.data;
  },

  /**
   * Admin: Upload partner logo (V2 - tenant-aware)
   */
  uploadLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/v2/partners/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Admin: Reorder partners (V2 - tenant-aware)
   */
  reorder: async (order) => {
    const response = await api.patch('/v2/partners/reorder', order);
    return response.data;
  },
};

// ==================== UPLOAD API (V2) ====================

export const uploadApi = {
  /**
   * Upload single image (V2 - tenant-aware)
   */
  image: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/v2/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Upload multiple images (V2 - tenant-aware)
   */
  images: async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await api.post('/v2/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Upload partner logo (V2 - tenant-aware)
   */
  partnerLogo: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/v2/upload/partner-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Upload business image (V2 - tenant-aware)
   */
  businessImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/v2/upload/business-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Upload multiple business images (V2 - tenant-aware)
   */
  businessImages: async (files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await api.post('/v2/upload/business-images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

// ==================== PROFILE API ====================

export const profileApi = {
  getEmpreendedor: async () => {
    const response = await api.get('/profile/empreendedor');
    return response.data;
  },

  updateEmpreendedor: async (data) => {
    const response = await api.put('/profile/empreendedor', data);
    return response.data;
  },

  getBusinesses: async () => {
    // Usa /my-businesses (não /profile/businesses) porque este retorna o
    // campo `manager_role` (OWNER/REPRESENTATIVE/COLLABORATOR) — necessário
    // para o Perfil renderizar botões contextuais (Editar/Gestores/Excluir).
    const response = await api.get('/my-businesses');
    return response.data;
  },
};

// ==================== REPORTS API ====================

export const reportsApi = {
  create: async (data) => {
    const response = await api.post('/reports', data);
    return response.data;
  },

  mine: async () => {
    const response = await api.get('/reports/mine');
    return response.data;
  },

  get: async (reportId) => {
    const response = await api.get(`/reports/${reportId}`);
    return response.data;
  },

  delete: async (reportId) => {
    const response = await api.delete(`/reports/${reportId}`);
    return response.data;
  },
};

// ==================== STREETS API (V2) ====================

export const streetsApi = {
  search: async (params) => {
    const response = await api.get('/v2/streets', { params });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/v2/streets', data);
    return response.data;
  },
  geocode: async (address, city, state, opts = {}) => {
    const params = { address };
    if (city) params.city = city;
    if (state) params.state = state;
    // CEP quando disponível — melhora precisão do Nominatim para endereços
    // ambíguos (ruas com nomes repetidos em bairros diferentes).
    if (opts.cep) params.cep = String(opts.cep).replace(/\D/g, '');
    const response = await api.get('/v2/streets/geocode', { params });
    return response.data;
  },
};

// ==================== LEGACY API (V1 compatibility) ====================

export const legacyApi = {
  /**
   * Make raw API call (for endpoints not yet migrated to v2)
   */
  get: (endpoint, params) => api.get(endpoint, { params }),
  post: (endpoint, data, config) => api.post(endpoint, data, config),
  put: (endpoint, data, config) => api.put(endpoint, data, config),
  patch: (endpoint, data, config) => api.patch(endpoint, data, config),
  delete: (endpoint, config) => api.delete(endpoint, config),
};

// Export the raw axios instance for special cases
export { api };

export default {
  businesses: businessesApi,
  alerts: alertsApi,
  programs: programsApi,
  categories: categoriesApi,
  bairros: bairrosApi,
  noticias: noticiasApi,
  promotions: promotionsApi,
  jobs: jobsApi,
  search: searchApi,
  social: socialApi,
  analytics: analyticsApi,
  reviews: reviewsApi,
  favorites: favoritesApi,
  comments: commentsApi,
  notifications: notificationsApi,
  municipios: municipiosApi,
  territories: territoriesApi,
  dashboard: dashboardApi,
  profile: profileApi,
  reports: reportsApi,
  legacy: legacyApi,
  // Admin APIs
  usersAdmin: usersAdminApi,
  bannersAdmin: bannersAdminApi,
  partnersAdmin: partnersAdminApi,
  reportsAdmin: reportsAdminApi,
  moderationAdmin: moderationAdminApi,
  claimsAdmin: claimsAdminApi,
  municipiosAdmin: municipiosAdminApi,
  noticiasAdmin: noticiasAdminApi,
  emailAdmin: emailAdminApi,
  territoriesAdmin: territoriesAdminApi,
  configAdmin: configAdminApi,
  contactAdmin: contactAdminApi,
  alertsAdmin: alertsAdminApi,
  programsAdmin: programsAdminApi,
  businessesAdmin: businessesAdminApi,
  notificationsAdmin: notificationsAdminApi,
  auditLogsAdmin: auditLogsAdminApi,
  systemAlerts: systemAlertsApi,
  alertRules: alertRulesApi,
  rbac: rbacApi,
  featureFlags: featureFlagsApi,
  password: passwordApi,
  // New V2 APIs
  emailTemplatesAdmin: emailTemplatesAdminApi,
  contentModerationAdmin: contentModerationAdminApi,
  territoriesAdminV2: territoriesAdminApiV2,
  pagesAdmin: pagesAdminApi,
  upload: uploadApi,
  bannersV2: bannersApiV2,
  partnersV2: partnersApiV2,
  heroBanners: heroBannersApi,
  bannerAnalytics: bannerAnalyticsApi,
  verificationConfig: {
    get: async () => (await api.get('/admin/verification-config')).data,
    update: async (grace_period_days) =>
      (await api.put('/admin/verification-config', { grace_period_days })).data,
    listUnverifiedHolds: async (params = {}) =>
      (await api.get('/admin/verification/unverified-holds', { params })).data,
  },
  getTenantId,
};

export const verificationConfigApi = {
  get: async () => (await api.get('/admin/verification-config')).data,
  update: async (grace_period_days) =>
    (await api.put('/admin/verification-config', { grace_period_days })).data,
  listUnverifiedHolds: async (params = {}) =>
    (await api.get('/admin/verification/unverified-holds', { params })).data,
};
